import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  useRef,
  lazy,
  Suspense,
} from 'react';
import './App.css';
import './pages/SalesPages/AuthDialogChrome.css';
import './components/NavbarRedesign.css';
import './components/NavbarLogoLockup.css';
import { Container, Navbar } from 'react-bootstrap';
import botspotLogo from './assets/botspot_logo_horizontal.png';
import Nav from 'react-bootstrap/Nav';
import {
  Routes,
  Route,
  Link,
  useNavigate,
  useLocation,
  NavLink,
  Navigate,
  type Location as RouterLocation,
} from 'react-router-dom';
import BotDetails from './pages/BotDetails';
import Terms from './pages/Terms';
import AddBot from './pages/AddBot';
import ProtectedRouteBase from './components/ProtectedRoute';
import SharePage from './pages/SharePage'; // Dedicated share page for viewing shared strategies
import ShareRedirectHandler from './components/ShareRedirectHandler'; // Handles backward compatibility for old share URLs
import SalesPage from './pages/SalesPages/SalesPage';
import SalesPage05 from './pages/SalesPages/SalesPage05';
import SalesPage06 from './pages/SalesPages/SalesPage06';
import SalesPage07Base from './pages/SalesPages/SalesPage07';
import GoogleSearchLandingPage from './pages/SalesPages/GoogleSearchLandingPage';
import AuthFirstDialog from './pages/SalesPages/AuthFirstDialog';
import SalesPage08 from './pages/SalesPages/SalesPage08';
import SalesChoosePage from './pages/SalesPages/SalesChoosePage';
import PromptGuide from './pages/SalesPages/PromptGuide';
import SalesAiPromptPage from './pages/SalesPages/SalesAiPromptPage';
import useABTest from './hooks/useABTest';
import { ACTIVE_EXPERIMENTS } from './utils/experiments';
import CheckoutPage from './pages/Checkout/CheckoutPage';
import CheckoutAuthPage from './pages/Checkout/CheckoutAuthPage';
import HostedCheckoutReturnPage from './pages/Checkout/HostedCheckoutReturnPage';
import PlanCheckoutPage, {
  CheckoutRedesignLoadingShell,
} from './pages/Checkout/StarterCheckoutPage';
import PostSignupDecisionPage from './pages/PostSignupDecisionPage';
import AutoLoginHandler from './components/AutoLoginHandler';
import AutoLoginSentPage from './pages/AutoLoginSentPage';
import SetPasswordPage from './pages/SetPasswordPage';
import UpgradeToProPage from './pages/UpgradeToPro/UpgradeToProPage'; // Import the new page
import VideoPage from './pages/VideoPage/VideoPage'; // <-- IMPORT NEW VIDEO PAGE
import SignupSurvey from './components/SignupSurvey/SignupSurvey'; // Import the new SignupSurvey component
import ModalTestPage from './pages/ModalTestPage'; // Import the modal test page
import NotFound from './pages/NotFound';
import Loading from './components/Loading';
import ReauthOverlay from './components/ReauthOverlay';
import ReauthOverlayErrorBoundary from './components/ReauthOverlayErrorBoundary';
import { usePaymentStatus } from './contexts/PaymentStatusContext';
import { useUnifiedAuth } from './contexts/UnifiedAuthContext'; // <-- UNIFIED AUTH SYSTEM
import { CheckoutCartProvider } from './contexts/CheckoutCartContext';
import {
  capturePostHogEvent,
  pushEvent,
  ANALYTICS_EVENTS,
  resetAnalyticsIdentity,
  setUserIdentity,
  POSTHOG_KEY,
} from './utils/analytics';
import { trackUtmParameters } from './utils/utmTracker';
import { buildMcpOauthRelayRedirect } from './utils/mcpOauthRelay';
import { initDebugging } from './utils/utmDebugger'; // <-- ADDED UTM DEBUGGER
import {
  safeGetStorageItem,
  safeRemoveStorageItem,
  safeSetStorageItem,
} from './utils/browserStorage';
import {
  buildAuthOverlayRouteState,
  canUseStoredAuthOverlayBackgroundLocation,
  getDefaultAuthOverlayBackgroundLocation,
  getAuthOverlayBackgroundLocation,
  getStoredAuthOverlayBackgroundLocation,
  rememberAuthOverlayBackgroundLocation,
} from './utils/authOverlayNavigation';
import { applyThemeMode, cn, useThemeMode } from './design-system';
import { SidebarProvider } from './design-system/sidebar';
import AuthenticatedSidebar, {
  AUTHENTICATED_SIDEBAR_FEATURE_FLAG,
} from './components/AuthenticatedSidebar/AuthenticatedSidebar';
import AppFooter from './components/AppFooter/AppFooter';
import SpotChatWidget from './components/SpotChatWidget';
import NavMenuDropdown, { type NavMenuDropdownItem } from './components/NavMenuDropdown';
import PublicRouteSeo from './components/PublicRouteSeo';
import NoindexRouteSeo from './components/NoindexRouteSeo';
import { HelmetProvider } from 'react-helmet-async';
import fetchWithTimeout from './utils/fetchWithTimeout';
import { isPreviewExperimentOverrideHost } from './utils/experimentOverrideHosts';
import {
  EXPERIMENT_PREVIEW_OVERRIDE_EVENT,
  getExperimentPreviewOverride,
  setExperimentPreviewOverride,
} from './utils/experimentPreviewOverrides';
import { initHotjar } from './utils/hotjar';
import {
  createCheckoutOverlayState,
  getCheckoutOverlayBackground,
  isCheckoutOverlayPath,
  locationToPath,
  isPlanCheckoutPath,
  navigateToCheckoutOverlay,
} from './utils/checkoutOverlayNavigation';
import { CHECKOUT_AUTH_PATH } from './utils/checkoutAuthNavigation';
import {
  getActivePostSignupDestination,
  hasActivePostSignupJourney,
} from './utils/postSignupJourney';
import {
  Bot as AgentIcon,
  ChartNoAxesCombined as DashboardIcon,
  CircleUserRound as AccountIcon,
  GraduationCap as EducationIcon,
  Store as MarketplaceIcon,
  X as XIcon,
} from 'lucide-react';
import BacktestPage from './pages/BacktestPage';
import BacktestChartPageBase from './pages/BacktestChartPage';
import DeployPage from './pages/DeployPage';
import PaywallModalDemo from './pages/PaywallModalDemo';
import Help from './pages/Help';
import WeeklyWorkshop from './pages/courses/WeeklyWorkshop';
import CoursePage from './pages/courses/CoursePage';
import CourseNameExperimentPage from './pages/courses/CourseNameExperimentPage';
import CourseSelfPacedPage from './pages/courses/CourseSelfPacedPage';
import ChallengePage from './pages/challenges/ChallengePage';
import ChallengeBookingPage from './pages/challenges/ChallengeBookingPage';
import ChallengeCallConfirmedPage from './pages/challenges/ChallengeCallConfirmedPage';
import NextChallengePage from './pages/challenges/NextChallengePage';
import ChallengeClarityReview from './pages/challenges/ChallengeClarityReview';
import CourseClarityReview from './pages/courses/CourseClarityReview';
import AIBootcampProPortal from './pages/courses/AIBootcampProPortal';
import LiveAlgoLabSalesPage from './pages/courses/LiveAlgoLabSalesPage';
import LiveAlgoLabWatchPage from './pages/courses/LiveAlgoLabWatchPage';
import EducationDesignExperiment from './pages/education/EducationDesignExperiment';
import ChoosePlanPage from './pages/ChoosePlanPage';
import PricingPage from './pages/PricingPage';
import AgentsPage from './pages/AgentsPage';
import TrustPage from './pages/TrustPage';
import ComparisonPage from './pages/ComparisonPage';
import AccountSettings from './pages/AccountSettings/AccountSettings';
import CartAbandonFeedbackPage from './pages/Feedback/CartAbandonFeedbackPage';
import EmailUnsubscribePage from './pages/EmailUnsubscribe/EmailUnsubscribePage';
import BlogIndex from './pages/blogs/BlogIndex';
import BlogPostPage from './pages/blogs/BlogPostPage';
import UsingAIToMakeAProfitableSwingTradingBotInSeconds from './pages/blogs/UsingAIToMakeAProfitableSwingTradingBotInSeconds'; // Updated path

import Privacy from './pages/Privacy';
import AutoLoginTest from './pages/AutoLoginTest';
import SeamlessLoginDemo from './pages/SeamlessLoginDemo';
import SchwabSuccess from './pages/oauth/SchwabSuccess';
import AlpacaSuccess from './pages/oauth/AlpacaSuccess';
import AlpacaMobileReturnFallback from './pages/mobile/AlpacaMobileReturnFallback';
import TradierSuccess from './pages/oauth/TradierSuccess';
import ConnectDiscord from './pages/connect/ConnectDiscord';
import ConnectTelegram from './pages/connect/ConnectTelegram';
import DeploymentRedirectPage from './pages/DeploymentRedirectPage';
import ActionAuthorizationPage from './pages/ActionAuthorizationPage';
import { initAnalytics } from './utils/analytics'; // Import analytics init
import useUser from './hooks/useUser'; // <-- UPDATED import path
import InvestorPage from './pages/InvestorPages/InvestorPage';
import AffiliatePage from './pages/Affiliate/AffiliatePage';
import { captureAffiliateReferralClick, getAffiliateCodeForApi } from './utils/utmTracker';
import { resolveRootLandingRoute } from './utils/rootLandingRoute';
import {
  hasPersistedAuthSessionHint,
  prefetchPublicHomepage,
} from './utils/persistedAuthSessionHint';

// ⚡ PERFORMANCE: Lazy load pages to reduce initial bundle size and improve TTI
const Home = lazy(() => import('./pages/Home'));
const HomeWithTrustStrip = lazy(() => import('./pages/HomeWithTrustStrip'));
const ControlRoomHome = lazy(() => import('./pages/ControlRoomHome'));
const ClarityHome = lazy(() => import('./pages/ClarityHome'));
const loadHomepageExperiment = () =>
  import(
    /* webpackPrefetch: true */
    './pages/HomepageExperiment'
  );
const HomepageExperiment = lazy(loadHomepageExperiment);
const HomeLovable = lazy(() => import('./pages/HomeLovable'));
prefetchPublicHomepage(window.location.pathname, loadHomepageExperiment);
const HomeV3 = lazy(() => import('./pages/HomeV3'));
const HomeV4 = lazy(() => import('./pages/HomeV4'));
const MarketplacePage = lazy(() => import('./pages/MarketplaceStrategy/MarketplacePage'));
const StrategyDetailsPage = lazy(() => import('./pages/StrategyDetails/StrategyDetailsPage'));
const StrategySettingsPage = lazy(() => import('./pages/MarketplaceStrategy/StrategySettingsPage'));
const MarketplaceAccountRedirectPage = lazy(
  () => import('./pages/MarketplaceStrategy/MarketplaceAccountRedirectPage'),
);
const EnterprisePage = lazy(() => import('./pages/EnterprisePage'));
const FamilyOfficeAiAuditPage = lazy(() => import('./pages/FamilyOfficeAiAuditPage'));
const SampleComplexBacktestPage = lazy(() => import('./pages/SampleComplexBacktestPage'));
const SalesMarketplacePage = lazy(() => import('./pages/SalesPages/SalesMarketplacePage'));
const Contest = lazy(() => import('./pages/Contest/Contest'));
const ContestRegister = lazy(() => import('./pages/Contest/ContestRegister'));
const UserProfilePage = lazy(() => import('./pages/ProfilePage'));
const MyDeploymentsPage = lazy(() => import('./pages/MyDeploymentsPage'));
const MyBacktestPage = lazy(() => import('./pages/MyBacktests/MyBacktestPage'));
const MyStrategies = lazy(() => import('./pages/MyStrategies/MyStrategies'));
const BrokerAccountsPage = lazy(() => import('./pages/BrokerAccounts/BrokerAccountsPage'));
const BrokerAccountDetailsPage = lazy(
  () => import('./pages/BrokerAccounts/BrokerAccountDetailsPage'),
);
const AuthenticatedDashboard = lazy(
  () => import('./pages/AuthenticatedDashboard/AuthenticatedDashboard'),
);
const loadAiAgent = () => import('./pages/AiAgent/AiAgent');
const AiAgent = lazy(loadAiAgent);

type AuthUser = {
  id?: string;
  sub?: string;
  email?: string;
  name?: string;
  given_name?: string;
  nickname?: string;
  created_at?: string;
};

export const getCanonicalAnalyticsUserId = (user?: AuthUser | null): string | undefined =>
  user?.id || user?.sub || user?.email || undefined;

export const getAnalyticsLoginMethod = (subject?: string): 'Google' | 'Email' | 'Unknown' => {
  if (subject?.includes('google-oauth2')) return 'Google';
  if (subject?.includes('auth0')) return 'Email';
  return 'Unknown';
};

type UserProfile = {
  id?: string | number;
  email?: string;
  howFoundUs?: unknown;
  createdAt?: string;
  created_at?: string;
  subscriptionStatus?: string;
  customerType?: string;
  isPayingCustomer?: boolean;
  activeProducts?: Array<{ status?: string }>;
};

type AnalyticsIdentity = Record<string, string | boolean | undefined>;

type ABTestResult = {
  variant: string | boolean | null;
  isLoading: boolean;
  isExperimentActive: boolean;
};

type ABTestOptions = Record<string, string | boolean | readonly string[] | null | undefined>;

type ExperimentRenderExposureProperties = Record<string, string | boolean | null | undefined>;

type ExperimentRenderExposureOptions = {
  shouldTrack: boolean;
  experimentName: string;
  featureFlagKey: string;
  posthogVariant?: string | null;
  routeVariant?: string | null;
  surface: string;
  path: string;
  exposureSource?: string | null;
  extra?: ExperimentRenderExposureProperties;
};

const useExperimentRenderExposure = ({
  shouldTrack,
  experimentName,
  featureFlagKey,
  posthogVariant,
  routeVariant,
  surface,
  path,
  exposureSource,
  extra = {},
}: ExperimentRenderExposureOptions) => {
  const trackedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!shouldTrack || !POSTHOG_KEY || !posthogVariant || !exposureSource) {
      return;
    }

    const exposureKey = [
      experimentName,
      featureFlagKey,
      posthogVariant,
      routeVariant || posthogVariant,
      surface,
      path,
    ].join(':');

    if (trackedRef.current.has(exposureKey)) {
      return;
    }

    trackedRef.current.add(exposureKey);
    capturePostHogEvent('experiment_exposure', {
      experiment_name: experimentName,
      feature_flag: featureFlagKey,
      feature_flag_value: posthogVariant,
      variant: routeVariant || posthogVariant,
      is_experiment_active: true,
      exposure_source: exposureSource,
      surface,
      path,
      [`$feature/${featureFlagKey}`]: posthogVariant,
      ...extra,
    });
  }, [
    shouldTrack,
    experimentName,
    featureFlagKey,
    posthogVariant,
    routeVariant,
    surface,
    path,
    exposureSource,
    extra,
  ]);
};

const BOOTCAMP_CONTENT_PRODUCT_SLUGS = new Set(['ai-bootcamp', 'ai-bootcamp-self-paced']);
const CHECKOUT_DESIGN_VALID_VARIANTS = ['control', 'test'] as const;
type AuthenticatedNavigationVariant = 'control' | 'test';

const isAuthenticatedNavigationVariant = (
  value: string | null,
): value is AuthenticatedNavigationVariant => value === 'control' || value === 'test';

type CheckoutDesignVariant = (typeof CHECKOUT_DESIGN_VALID_VARIANTS)[number];

const isCheckoutDesignVariantValue = (
  value: string | null | undefined,
): value is CheckoutDesignVariant =>
  // SAFETY: Membership in readonly variant catalog establishes CheckoutDesignVariant.
  CHECKOUT_DESIGN_VALID_VARIANTS.includes(value as CheckoutDesignVariant);

// SAFETY: Legacy JS component props are narrowed to contracts used by this router.
const ProtectedRoute = ProtectedRouteBase as React.ComponentType<
  React.PropsWithChildren<{
    requirePayment?: boolean;
    adminOnly?: boolean;
    component?: React.ComponentType<unknown>;
    loadingFallback?: React.ReactNode;
    waitForPaymentStatus?: boolean;
  }>
>;

// SAFETY: Legacy JS sales page accepts this documented prop contract.
const SalesPage07 = SalesPage07Base as React.ComponentType<{
  signupVariant?: string;
  experimentContext?: BotSpotExperimentContext;
  trackSignupFramingExperiment?: boolean;
  onLoginClick?: () => void;
}>;

// SAFETY: Legacy JS page is rendered without props.
const BacktestChartPage = BacktestChartPageBase as React.ComponentType;
// SAFETY: Legacy checkout page accepts this documented route prop contract.
const PlanCheckoutRoutePage = PlanCheckoutPage as React.ComponentType<{
  presentation?: 'page' | 'overlay';
  checkoutDesignVariantOverride?: CheckoutDesignVariant;
  checkoutDesignExperimentActiveOverride?: boolean;
  checkoutThemeOverride?: string;
}>;
// SAFETY: Legacy checkout page accepts this documented route prop contract.
const CheckoutRoutePage = CheckoutPage as React.ComponentType<{
  presentation?: 'page' | 'overlay';
  checkoutDesignVariantOverride?: CheckoutDesignVariant;
  checkoutDesignExperimentActiveOverride?: boolean;
  checkoutThemeOverride?: string;
}>;

const CheckoutOverlayLoading = ({ checkoutTheme }: { checkoutTheme: string }) => (
  <CheckoutRedesignLoadingShell checkoutTheme={checkoutTheme} presentation="overlay" />
);

// SAFETY: Analytics adapter accepts normalized AnalyticsIdentity values.
const identifyUser = setUserIdentity as (userData: AnalyticsIdentity) => void;
// SAFETY: Legacy hook result is normalized by ABTestResult consumers below.
const useTypedABTest = useABTest as (
  experimentName: string,
  featureFlagKey: string,
  defaultVariant?: string,
  trackingData?: ABTestOptions,
) => ABTestResult;

function MarketplaceRouteFallback() {
  return (
    <div className="marketplace-route-fallback" aria-label="Loading marketplace">
      <div className="marketplace-route-fallback__inner">
        <div className="marketplace-route-fallback__title" />
        <div className="marketplace-route-fallback__filters">
          <div />
          <div />
          <div />
        </div>
        <div className="marketplace-route-fallback__table">
          {Array.from({ length: 6 }).map((_, index) => (
            <div className="marketplace-route-fallback__row" key={index}>
              <span />
              <span />
              <span />
              <span />
              <span />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function WorkspaceRouteFallback({ label }: { label: string }) {
  return (
    <div className="workspace-route-fallback" aria-label={`Loading ${label}`}>
      <div className="workspace-route-fallback__inner">
        <div className="workspace-route-fallback__meta" />
        <div className="workspace-route-fallback__header">
          <div className="workspace-route-fallback__title" />
          <div className="workspace-route-fallback__action" />
        </div>
        <div className="workspace-route-fallback__copy" />
        <div className="workspace-route-fallback__bar" />
        <div className="workspace-route-fallback__rows">
          {Array.from({ length: 5 }).map((_, index) => (
            <div className="workspace-route-fallback__row" key={index}>
              <span />
              <span />
              <span />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProtectedWorkspaceRoute({
  children,
  label,
}: {
  children: React.ReactNode;
  label: string;
}): React.ReactElement {
  return (
    <ProtectedRoute>
      <Suspense fallback={<WorkspaceRouteFallback label={label} />}>{children}</Suspense>
    </ProtectedRoute>
  );
}

function AiAgentRouteFallback() {
  return (
    <div className="ai-agent-route-fallback" aria-label="Loading AI Agent">
      <div className="ai-agent-route-fallback__sidebar">
        <div className="ai-agent-route-fallback__title" />
        {Array.from({ length: 6 }).map((_, index) => (
          <div className="ai-agent-route-fallback__nav-row" key={index}>
            <span />
            <span />
          </div>
        ))}
      </div>
      <div className="ai-agent-route-fallback__workspace">
        <div className="ai-agent-route-fallback__toolbar">
          <span />
          <span />
        </div>
        <div className="ai-agent-route-fallback__message ai-agent-route-fallback__message--wide" />
        <div className="ai-agent-route-fallback__message" />
        <div className="ai-agent-route-fallback__composer" />
      </div>
    </div>
  );
}

const hasProfileAttribution = (profile?: UserProfile | null): boolean =>
  Boolean(String(profile?.howFoundUs || '').trim());

const SALES_SIGNUP_V4_POSTHOG_VARIANTS = ['control', 'email_only', 'auth_first'];
const SALES_SIGNUP_V4_ROUTE_VARIANTS = ['auth_first', 'email_only', 'full_form'];
const SALES_AUTH_FIRST_DEFAULT_RETURN_TO = '/dashboard';
const BOTSPOT_SALES_EXPERIMENT_HOSTS = new Set([
  'botspot.trade',
  'www.botspot.trade',
  'dev.botspot.trade',
  'localhost',
  '127.0.0.1',
  '::1',
]);
const SALES_SIGNUP_V4_VARIANT_ALIASES = new Map([
  ['control', 'full_form'],
  ['login_menu', 'auth_first'],
]);
const SALES_SIGNUP_V4_POSTHOG_VARIANT_ALIASES = new Map([
  ['control', 'control'],
  ['full_form', 'control'],
  ['email_only', 'email_only'],
  ['auth_first', 'auth_first'],
  ['login_menu', 'auth_first'],
]);
const SALES_SIGNUP_V5_DEFAULT_VARIANT = 'control';
const SALES_SIGNUP_V5_POSTHOG_VARIANTS = ['control', 'auth_first'];
const SALES_SIGNUP_V5_ACCEPTED_FLAG_VARIANTS = [...SALES_SIGNUP_V5_POSTHOG_VARIANTS, 'email_only'];
const SALES_SIGNUP_V5_ROUTE_VARIANTS = ['auth_first', 'email_only'];
const SALES_SIGNUP_V5_FALLBACK_SEED_KEY = 'sales_page_signup_framing_v5:fallback_seed';
const SALES_SIGNUP_V5_VARIANT_ALIASES = new Map([['control', 'email_only']]);
const SALES_SIGNUP_V5_POSTHOG_VARIANT_ALIASES = new Map([
  ['control', 'control'],
  ['email_only', 'control'],
  ['auth_first', 'auth_first'],
]);

const hashStringToPositiveInt = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
};

const getSalesSignupV5FallbackSeed = (): string => {
  try {
    const storedSeed = window.localStorage?.getItem(SALES_SIGNUP_V5_FALLBACK_SEED_KEY);
    if (storedSeed) {
      return storedSeed;
    }

    const generatedSeed = `${Date.now()}:${Math.random().toString(36).slice(2)}`;
    window.localStorage?.setItem(SALES_SIGNUP_V5_FALLBACK_SEED_KEY, generatedSeed);
    return generatedSeed;
  } catch {
    return `${window.location.pathname}:${window.location.search}`;
  }
};

const getStableSalesSignupV5FallbackVariant = (): string => {
  const seed = getSalesSignupV5FallbackSeed();
  return (
    SALES_SIGNUP_V5_POSTHOG_VARIANTS[
      hashStringToPositiveInt(seed) % SALES_SIGNUP_V5_POSTHOG_VARIANTS.length
    ] || SALES_SIGNUP_V5_DEFAULT_VARIANT
  );
};

const isSafeInternalPath = (value: string): boolean =>
  value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\');

const toInternalPath = (url: URL): string => `${url.pathname}${url.search}${url.hash}` || '/';

const toLocationPath = (
  targetLocation: Pick<RouterLocation, 'pathname' | 'search' | 'hash'>,
): string =>
  `${targetLocation.pathname}${targetLocation.search || ''}${targetLocation.hash || ''}` || '/';

const getActionAuthorizationIdFromPath = (pathname: string): string | null => {
  const match = pathname.match(/^\/authorize-action\/([^/?#]+)/);
  if (!match?.[1]) {
    return null;
  }

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

// SAFETY: Legacy authorization page supports explicit action-id override.
const ActionAuthorizationBackdropPage = ActionAuthorizationPage as React.ComponentType<{
  actionIdOverride?: string;
}>;

type SalesAuthReturnToMetadata = Record<string, string | null | undefined>;

const appendSalesAuthReturnToMetadata = (
  returnTo: string,
  metadata?: SalesAuthReturnToMetadata | null,
): string => {
  if (!metadata) {
    return returnTo;
  }

  const parsed = new URL(returnTo, window.location.origin);
  Object.entries(metadata).forEach(([key, value]) => {
    const trimmedValue = value?.trim();
    if (trimmedValue) {
      parsed.searchParams.set(key, trimmedValue);
    }
  });
  return toInternalPath(parsed);
};

const normalizeSalesAuthReturnTo = (returnTo?: string | null): string => {
  const trimmedReturnTo = returnTo?.trim();
  if (!trimmedReturnTo) {
    return '/';
  }

  if (isSafeInternalPath(trimmedReturnTo) || /^https?:\/\//i.test(trimmedReturnTo)) {
    try {
      const parsed = new URL(trimmedReturnTo, window.location.origin);
      return parsed.origin === window.location.origin ? toInternalPath(parsed) : '/';
    } catch {
      return '/';
    }
  }

  return '/';
};

const getAuthOverlayBackgroundLocationFromPath = (path?: string | null): RouterLocation | null => {
  const normalizedPath = normalizeSalesAuthReturnTo(path);

  try {
    const parsed = new URL(normalizedPath, window.location.origin);
    const backgroundLocation: RouterLocation = {
      pathname: parsed.pathname,
      search: parsed.search,
      hash: parsed.hash,
      state: null,
      key: 'auth-overlay-return-to',
    };

    return canUseStoredAuthOverlayBackgroundLocation(backgroundLocation)
      ? backgroundLocation
      : null;
  } catch {
    return null;
  }
};

const getUsableAuthOverlayBackgroundLocation = (
  location?: RouterLocation | null,
): RouterLocation | null => {
  if (!location || !canUseStoredAuthOverlayBackgroundLocation(location)) {
    return null;
  }

  return location;
};

export const buildSalesAuthReturnToPath = (
  returnTo?: string | null,
  metadata?: SalesAuthReturnToMetadata | null,
): string => {
  const normalizedReturnTo = normalizeSalesAuthReturnTo(returnTo);
  return appendSalesAuthReturnToMetadata(normalizedReturnTo, metadata);
};

export const buildSalesAuthLoginPath = (
  returnTo?: string | null,
  metadata?: SalesAuthReturnToMetadata | null,
): string => {
  const attributedReturnTo = buildSalesAuthReturnToPath(returnTo, metadata);
  return `/login?returnTo=${encodeURIComponent(attributedReturnTo)}`;
};

export const getSalesAuthFirstReturnTo = (successUrl?: string | null): string =>
  successUrl || SALES_AUTH_FIRST_DEFAULT_RETURN_TO;

export const hasResolvedSalesSignupV4Variant = ({
  initialFrozenVariant,
  frozenVariant,
  isExperimentLoading,
}: {
  initialFrozenVariant?: string | null;
  frozenVariant?: string | null;
  isExperimentLoading: boolean;
}): boolean => Boolean(initialFrozenVariant || frozenVariant || !isExperimentLoading);

export const buildSalesAiAgentEntryCompatibilityPath = (search = '', hash = ''): string => {
  const params = new URLSearchParams(search);
  const legacyVariant = params.get('salesAuthVariant');
  params.delete('salesAuthVariant');

  if (!params.has('showLogin')) {
    params.set('showLogin', '1');
  }

  if (!params.has('successUrl')) {
    params.set('successUrl', `${window.location.origin}/ai-agent`);
  }

  if (legacyVariant === 'auth_first') {
    params.set('signupV4', 'auth_first');
  }

  const queryString = params.toString();
  return `/sales${queryString ? `?${queryString}` : ''}${hash}`;
};

const SalesAiAgentEntryCompatibilityRedirect = () => {
  const location = useLocation();
  return (
    <Navigate
      to={buildSalesAiAgentEntryCompatibilityPath(location.search, location.hash)}
      replace
    />
  );
};

export const buildEducationCompatibilityPath = (
  targetPath: string,
  search = '',
  hash = '',
): string => `${targetPath}${search}${hash}`;

const EducationCompatibilityRedirect = ({ to }: { to: string }) => {
  const location = useLocation();
  return (
    <Navigate to={buildEducationCompatibilityPath(to, location.search, location.hash)} replace />
  );
};

type ExperimentFlagValue = string | boolean | null | undefined;

export const normalizeSalesSignupV4Variant = (value: ExperimentFlagValue): string | null => {
  if (value?.constructor !== String) {
    return null;
  }

  const normalizedValue = SALES_SIGNUP_V4_VARIANT_ALIASES.get(value) || value;
  return SALES_SIGNUP_V4_ROUTE_VARIANTS.includes(normalizedValue) ? normalizedValue : null;
};
export const toSalesSignupV4PostHogVariant = (value: ExperimentFlagValue): string | null => {
  if (value?.constructor !== String) {
    return null;
  }

  const normalizedValue = SALES_SIGNUP_V4_POSTHOG_VARIANT_ALIASES.get(value) || value;
  return SALES_SIGNUP_V4_POSTHOG_VARIANTS.includes(normalizedValue) ? normalizedValue : null;
};
export const normalizeSalesSignupV5Variant = (value: ExperimentFlagValue): string | null => {
  if (value?.constructor !== String) {
    return null;
  }

  const normalizedValue = SALES_SIGNUP_V5_VARIANT_ALIASES.get(value) || value;
  return SALES_SIGNUP_V5_ROUTE_VARIANTS.includes(normalizedValue) ? normalizedValue : null;
};
export const toSalesSignupV5PostHogVariant = (value: ExperimentFlagValue): string | null => {
  if (value?.constructor !== String) {
    return null;
  }

  const normalizedValue = SALES_SIGNUP_V5_POSTHOG_VARIANT_ALIASES.get(value) || value;
  return SALES_SIGNUP_V5_POSTHOG_VARIANTS.includes(normalizedValue) ? normalizedValue : null;
};
const isValidSalesSignupV5Variant = (value: ExperimentFlagValue): value is string =>
  normalizeSalesSignupV5Variant(value) !== null;

export const isBotSpotSalesExperimentHost = (
  hostname = globalThis.window?.location.hostname || '',
): boolean => BOTSPOT_SALES_EXPERIMENT_HOSTS.has(hostname);

const getSynchronousSalesSignupV5FlagVariant = (featureFlagKey: string): string | null => {
  try {
    // SAFETY: Optional PostHog global is read-only and validated before invocation.
    const posthog = (
      window as typeof window & {
        posthog?: {
          getFeatureFlag?: (flagKey: string) => ExperimentFlagValue;
        };
      }
    ).posthog;

    if (!posthog?.getFeatureFlag) {
      return null;
    }

    return normalizeSalesSignupV5Variant(posthog.getFeatureFlag(featureFlagKey));
  } catch {
    return null;
  }
};

const myCustomLogger = console.info;

function AppContent() {
  /**
   * UNIFIED AUTHENTICATION SYSTEM
   *
   * This component now uses the UnifiedAuthContext which consolidates:
   * 1. Auth0 authentication (traditional OAuth login/signup)
   * 2. Custom JWT authentication (seamless post-purchase login)
   *
   * The unified auth system automatically:
   * - Detects auto-login tokens in URL parameters
   * - Validates and processes custom JWT tokens
   * - Provides appropriate API tokens for backend requests
   * - Maintains consistent user state across both auth methods
   */
  const {
    user,
    isAuthenticated,
    isLoading: authLoading,
    authMethod,
    getApiToken,
  } = useUnifiedAuth();

  /**
   * USER PROFILE AND ADMIN STATUS
   * Get additional user data including admin status and payment info
   */
  const {
    isPayingCustomer,
    paymentLoading,
    userProfile,
    profileLoading,
    refreshProfile,
    logout: customLogout,
  } = useUser();
  // SAFETY: useUser owns UserProfile normalization at provider boundary.
  const resolvedUserProfile = userProfile as UserProfile | null;
  const { productSlugs = [] } = usePaymentStatus();
  const hasBootcampContentAccess =
    Array.isArray(productSlugs) &&
    productSlugs.some((slug) => BOOTCAMP_CONTENT_PRODUCT_SLUGS.has(String(slug).toLowerCase()));
  /**
   * UNIFIED AUTHENTICATION LOGGING
   * Track authentication state changes for debugging and analytics
   */
  const identifiedAnalyticsUserRef = useRef<string | null>(null);
  useEffect(() => {
    console.debug('[AppContent] Unified auth state:', {
      isAuthenticated,
      authMethod,
      userEmail: user?.email,
      isLoading: authLoading,
    });

    if (authLoading) {
      return;
    }

    const currentUserId = isAuthenticated ? getCanonicalAnalyticsUserId(user) : null;
    if (currentUserId) {
      if (
        identifiedAnalyticsUserRef.current &&
        identifiedAnalyticsUserRef.current !== currentUserId
      ) {
        resetAnalyticsIdentity();
      }
      identifiedAnalyticsUserRef.current = currentUserId;
    } else {
      identifiedAnalyticsUserRef.current = null;
    }
  }, [isAuthenticated, authMethod, user, authLoading]);

  const navigate = useNavigate();
  const location = useLocation();
  const rootLandingRoute = resolveRootLandingRoute({
    authLoading,
    isAuthenticated,
    hasPersistedSessionHint: hasPersistedAuthSessionHint(location.search),
  });
  const aiBootcampEducationPath = hasBootcampContentAccess
    ? '/ai-bot-builder-bootcamp/watch'
    : '/courses/ai-trading-bootcamp';
  const challengesEducationPath = '/challenges';
  const signupV4ExposureResolutionRef = useRef({
    pathname: location.pathname,
    resolvedThisPageView: false,
  });
  if (signupV4ExposureResolutionRef.current.pathname !== location.pathname) {
    signupV4ExposureResolutionRef.current = {
      pathname: location.pathname,
      resolvedThisPageView: false,
    };
  }
  const isCheckoutRoute = isCheckoutOverlayPath(location.pathname);
  const isCheckoutAuthRoute = location.pathname === CHECKOUT_AUTH_PATH;
  const previousNonCheckoutLocationRef = useRef<ReturnType<typeof useLocation> | null>(
    isCheckoutRoute ? null : location,
  );

  useEffect(() => {
    if (!isCheckoutRoute) {
      previousNonCheckoutLocationRef.current = location;
    }
  }, [isCheckoutRoute, location]);

  const explicitCheckoutOverlayBackground = useMemo(() => {
    if (!isCheckoutRoute) {
      return null;
    }
    return getCheckoutOverlayBackground(location.state);
  }, [isCheckoutRoute, location.state]);
  const implicitCheckoutOverlayBackground = useMemo(() => {
    if (!isCheckoutRoute || explicitCheckoutOverlayBackground) {
      return null;
    }

    const implicitOverlayState = createCheckoutOverlayState(previousNonCheckoutLocationRef.current);
    return getCheckoutOverlayBackground(implicitOverlayState);
  }, [explicitCheckoutOverlayBackground, isCheckoutRoute]);
  const fallbackCheckoutOverlayBackground = useMemo(() => {
    if (
      !isCheckoutRoute ||
      explicitCheckoutOverlayBackground ||
      implicitCheckoutOverlayBackground
    ) {
      return null;
    }

    return {
      pathname: '/pricing',
      search: '',
      hash: '',
      state: null,
      key: 'checkout-fallback-background',
    };
  }, [explicitCheckoutOverlayBackground, implicitCheckoutOverlayBackground, isCheckoutRoute]);
  const rawCheckoutOverlayBackground =
    explicitCheckoutOverlayBackground ||
    implicitCheckoutOverlayBackground ||
    fallbackCheckoutOverlayBackground;
  const checkoutDesignOverride = useMemo(() => {
    if (!isPreviewExperimentOverrideHost()) {
      return undefined;
    }

    const queryParams = new URLSearchParams(location.search);
    const queryVariant = queryParams.get('checkoutDesignVariant');
    return isCheckoutDesignVariantValue(queryVariant) ? queryVariant : undefined;
  }, [location.search]);
  const checkoutOverlayExperiment = ACTIVE_EXPERIMENTS.CHECKOUT_REDESIGN_2026_07;
  // Failed checkout-redesign experiment is retired. Preview query overrides remain
  // available for visual regression work, but production users stay on control.
  const shouldResolveCheckoutOverlayVariant = false;
  const checkoutOverlayTrackingData = useMemo(
    () => ({
      page: 'checkout_overlay_shell',
      checkout_surface: isCheckoutAuthRoute
        ? 'account_gate'
        : location.pathname === '/checkout'
          ? 'unified_cart_checkout'
          : 'plan_checkout',
      validVariants: CHECKOUT_DESIGN_VALID_VARIANTS,
      normalizeControlExposure: true,
      skipFeatureFlagEvaluation:
        !shouldResolveCheckoutOverlayVariant || Boolean(checkoutDesignOverride),
      forcedVariant:
        checkoutDesignOverride || (!shouldResolveCheckoutOverlayVariant ? 'control' : undefined),
    }),
    [
      checkoutDesignOverride,
      isCheckoutAuthRoute,
      location.pathname,
      shouldResolveCheckoutOverlayVariant,
    ],
  );
  const { variant: checkoutOverlayFlagVariant, isLoading: isCheckoutOverlayVariantLoading } =
    useTypedABTest(
      checkoutOverlayExperiment.name,
      checkoutOverlayExperiment.featureFlag || 'checkout-redesign-2026-07',
      'control',
      checkoutOverlayTrackingData,
    );
  const checkoutOverlayDesignVariant =
    checkoutDesignOverride || (checkoutOverlayFlagVariant === 'test' ? 'test' : 'control');
  const isCheckoutOverlayResolving =
    shouldResolveCheckoutOverlayVariant &&
    !checkoutDesignOverride &&
    isCheckoutOverlayVariantLoading;
  const shouldUseCheckoutOverlay =
    Boolean(rawCheckoutOverlayBackground) &&
    (isCheckoutAuthRoute || checkoutOverlayDesignVariant === 'test' || isCheckoutOverlayResolving);
  const checkoutOverlayBackground = shouldUseCheckoutOverlay ? rawCheckoutOverlayBackground : null;
  const checkoutDialogRef = useRef<HTMLDivElement | null>(null);
  const closeCheckoutOverlay = useCallback(() => {
    if (!checkoutOverlayBackground) {
      return;
    }
    navigate(locationToPath(checkoutOverlayBackground), {
      replace: true,
      state: checkoutOverlayBackground.state || null,
    });
  }, [checkoutOverlayBackground, navigate]);
  const routeLocation = checkoutOverlayBackground || location;
  const visibleLocation = routeLocation;
  const visiblePathname = visibleLocation.pathname;
  const [accountThemeMode] = useThemeMode();
  const accountMenuTheme = applyThemeMode(accountThemeMode);
  const headerLoginReturnTo = useMemo(() => {
    if (location.pathname === '/login') {
      return '/';
    }

    const path = `${location.pathname}${location.search}${location.hash}`;
    return path || '/';
  }, [location.hash, location.pathname, location.search]);
  const headerLoginPath = useMemo(
    () => `/login?returnTo=${encodeURIComponent(headerLoginReturnTo)}&source=global_nav`,
    [headerLoginReturnTo],
  );
  const headerSignupPath = useMemo(
    () =>
      `/login?returnTo=${encodeURIComponent(headerLoginReturnTo)}&source=global_nav_signup&intent=signup`,
    [headerLoginReturnTo],
  );
  const authenticatedNavigationExperiment =
    ACTIVE_EXPERIMENTS.AUTHENTICATED_SIDEBAR_NAVIGATION_2026_08;
  const authenticatedNavigationUserId = isAuthenticated
    ? getCanonicalAnalyticsUserId(user)
    : undefined;
  const [authenticatedNavigationIdentityReady, setAuthenticatedNavigationIdentityReady] =
    useState(false);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !authenticatedNavigationUserId) {
      setAuthenticatedNavigationIdentityReady(false);
      return;
    }

    identifyUser({
      userId: authenticatedNavigationUserId,
      userEmail: user?.email,
      isAuthenticated: true,
    });
    setAuthenticatedNavigationIdentityReady(true);
  }, [authLoading, authenticatedNavigationUserId, isAuthenticated, user?.email]);

  const authenticatedNavigationQueryOverride = useMemo<
    AuthenticatedNavigationVariant | undefined
  >(() => {
    if (!isPreviewExperimentOverrideHost()) return undefined;
    const queryVariant = new URLSearchParams(location.search).get('authenticatedNavVariant');
    return isAuthenticatedNavigationVariant(queryVariant) ? queryVariant : undefined;
  }, [location.search]);
  const [authenticatedNavigationSessionOverride, setAuthenticatedNavigationSessionOverride] =
    useState<AuthenticatedNavigationVariant | undefined>(() => {
      const storedVariant = getExperimentPreviewOverride(AUTHENTICATED_SIDEBAR_FEATURE_FLAG, [
        'control',
        'test',
      ]);
      return isAuthenticatedNavigationVariant(storedVariant) ? storedVariant : undefined;
    });

  useEffect(() => {
    const syncSessionOverride = () => {
      const storedVariant = getExperimentPreviewOverride(AUTHENTICATED_SIDEBAR_FEATURE_FLAG, [
        'control',
        'test',
      ]);
      setAuthenticatedNavigationSessionOverride(
        isAuthenticatedNavigationVariant(storedVariant) ? storedVariant : undefined,
      );
    };

    window.addEventListener(EXPERIMENT_PREVIEW_OVERRIDE_EVENT, syncSessionOverride);
    return () => {
      window.removeEventListener(EXPERIMENT_PREVIEW_OVERRIDE_EVENT, syncSessionOverride);
    };
  }, []);

  useEffect(() => {
    if (!authenticatedNavigationQueryOverride || !isPreviewExperimentOverrideHost()) return;
    setExperimentPreviewOverride(
      AUTHENTICATED_SIDEBAR_FEATURE_FLAG,
      authenticatedNavigationQueryOverride,
      ['control', 'test'],
    );
  }, [authenticatedNavigationQueryOverride]);

  const authenticatedNavigationOverride =
    authenticatedNavigationQueryOverride || authenticatedNavigationSessionOverride;
  const authenticatedNavigationTrackingData = useMemo(
    () => ({
      surface: 'authenticated_app_shell',
      path: location.pathname,
      is_authenticated: isAuthenticated,
      validVariants: ['control', 'test'] as const,
      skipFeatureFlagEvaluation:
        !isAuthenticated ||
        !authenticatedNavigationIdentityReady ||
        Boolean(authenticatedNavigationOverride),
      forcedVariant:
        authenticatedNavigationOverride ||
        (!isAuthenticated || !authenticatedNavigationIdentityReady ? 'control' : undefined),
    }),
    [
      authenticatedNavigationIdentityReady,
      authenticatedNavigationOverride,
      isAuthenticated,
      location.pathname,
    ],
  );
  const {
    variant: authenticatedNavigationFlagVariant,
    isExperimentActive: isAuthenticatedNavigationExperimentActive,
  } = useTypedABTest(
    authenticatedNavigationExperiment.name,
    authenticatedNavigationExperiment.featureFlag || AUTHENTICATED_SIDEBAR_FEATURE_FLAG,
    'control',
    authenticatedNavigationTrackingData,
  );

  useEffect(() => {
    if (location.pathname === '/sales') {
      return;
    }

    rememberAuthOverlayBackgroundLocation(location);
  }, [location]);

  React.useEffect(() => {
    const affiliateCode = getAffiliateCodeForApi();
    if (affiliateCode) {
      console.info('[AffiliateReferral] captured affiliate code for session', {
        affiliateCode,
        pathname: location.pathname,
        search: location.search,
        hasApiUrl: Boolean(process.env.REACT_APP_API_URL),
      });
    }

    captureAffiliateReferralClick(process.env.REACT_APP_API_URL);
  }, [location.pathname, location.search]);

  // Define paths that should not show the header/footer
  const noHeaderPaths = [
    '/sales',
    '/sales05',
    '/sales-v2',
    '/lp/google-search',
    '/checkout',
    '/stripe/success',
    '/set-password',
    '/login',
    '/signup-survey',
    '/feedback',
    '/upgrade-to-pro',
    '/pro-success',
    '/mobile/alpaca-auth/return',
  ];

  // Define paths that should not show the footer only
  const noFooterPaths = ['/ai-agent', '/dashboard'];

  // Mobile detection for responsive header hiding
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [isNavbarMobileView, setIsNavbarMobileView] = useState(window.innerWidth < 1200);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
      setIsNavbarMobileView(window.innerWidth < 1200);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if on AI Agent page (for mobile header hiding)
  const isAiAgentPage = visiblePathname.startsWith('/ai-agent');
  const isDashboardPage = visiblePathname === '/dashboard';
  const isMarketplacePage = visiblePathname === '/marketplace';
  const isHomeLovableRoute = visiblePathname === '/home-lovable';

  // Check if current path should hide header
  // Hide header on mobile for AI Agent to maximize screen space
  const shouldHideHeader =
    noHeaderPaths.some((path) => visiblePathname.startsWith(path)) ||
    (isMobileView && isAiAgentPage);
  const shouldRenderHeader = !shouldHideHeader;
  const shouldRenderAuthenticatedSidebar =
    shouldRenderHeader &&
    isAuthenticated &&
    (authenticatedNavigationOverride === 'test' ||
      (isAuthenticatedNavigationExperimentActive && authenticatedNavigationFlagVariant === 'test'));

  // Check if current path should hide footer
  const shouldHideFooter =
    shouldHideHeader || noFooterPaths.some((path) => visiblePathname.startsWith(path));
  const isNavbarCollapsedView = isNavbarMobileView;
  // Education treatments scope their system-theme navbar tokens from their
  // rendered experiment root. App-level route checks would leak light mode to
  // control or unapproved Education pages.
  const navbarRedesignTheme = accountMenuTheme;

  // Add state to track navbar expansion
  const [expanded, setExpanded] = useState(false);
  const [isNavbarScrolled, setIsNavbarScrolled] = useState(false);
  const navbarCollapseRef = useRef<HTMLDivElement | null>(null);
  const navbarToggleRef = useRef<HTMLButtonElement | null>(null);
  const navbarOpenModalityRef = useRef<'keyboard' | 'pointer'>('keyboard');

  useEffect(() => {
    const updateNavbarScrollState = () => {
      const nextScrolled = window.scrollY > 8;
      setIsNavbarScrolled((current) => (current === nextScrolled ? current : nextScrolled));
    };

    updateNavbarScrollState();
    window.addEventListener('scroll', updateNavbarScrollState, {
      passive: true,
    });
    return () => window.removeEventListener('scroll', updateNavbarScrollState);
  }, []);

  // Hosted connector OAuth relay:
  // Auth0 redirects to botspot.trade, then we forward code/state/error to the client callback
  // encoded in mcp_oauth_redirect.
  useEffect(() => {
    const relayTarget = buildMcpOauthRelayRedirect(window.location.href);
    if (!relayTarget) {
      return;
    }

    try {
      const parsed = new URL(relayTarget);
      console.debug('[App] Relaying MCP OAuth callback', {
        targetHost: parsed.host,
      });
    } catch {
      console.debug('[App] Relaying MCP OAuth callback');
    }

    window.location.replace(relayTarget);
  }, []);

  // Scroll to top on page navigation. Account settings must override browser refresh restoration.
  useLayoutEffect(() => {
    if (checkoutOverlayBackground) {
      return undefined;
    }

    const windowPathname = window.location?.pathname || '';
    const isAccountSettingsPath =
      location.pathname.startsWith('/account-settings') ||
      windowPathname.startsWith('/account-settings');

    if (location.hash) {
      return undefined;
    }

    const canControlScrollRestoration =
      isAccountSettingsPath && 'scrollRestoration' in window.history;
    const previousScrollRestoration = canControlScrollRestoration
      ? window.history.scrollRestoration
      : null;
    if (canControlScrollRestoration) {
      window.history.scrollRestoration = 'manual';
    }

    const scrollToTop = (): void => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    };

    scrollToTop();

    const rafId =
      isAccountSettingsPath && window.requestAnimationFrame
        ? window.requestAnimationFrame(scrollToTop)
        : null;
    const timeoutId = isAccountSettingsPath ? window.setTimeout(scrollToTop, 50) : null;

    return () => {
      if (rafId !== null && window.cancelAnimationFrame) {
        window.cancelAnimationFrame(rafId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      if (canControlScrollRestoration && previousScrollRestoration) {
        window.history.scrollRestoration = previousScrollRestoration;
      }
    };
  }, [checkoutOverlayBackground, location.pathname, location.hash]);

  // Function to close navbar when navigation occurs
  const closeNavbar = () => setExpanded(false);

  // Collapsed navigation behaves like a modal surface: focus enters the menu,
  // stays inside it, and returns to the trigger when dismissed.
  useEffect(() => {
    if (!isNavbarCollapsedView || !expanded) {
      return undefined;
    }

    const collapse = navbarCollapseRef.current;
    const pageMain = document.querySelector<HTMLElement>('main');
    const previousMainAriaHidden = pageMain?.getAttribute('aria-hidden') ?? null;
    pageMain?.setAttribute('inert', '');
    pageMain?.setAttribute('aria-hidden', 'true');

    const focusableElements = (): HTMLElement[] => {
      const menuItems = collapse
        ? Array.from(
            collapse.querySelectorAll<HTMLElement>(
              'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ),
          )
        : [];
      return [navbarToggleRef.current, ...menuItems].filter((element): element is HTMLElement =>
        Boolean(element),
      );
    };

    // The expanded menu and its refs are committed before this effect runs.
    // Move keyboard focus immediately so the accessibility contract does not
    // depend on a later animation frame that may be delayed or cancelled.
    if (navbarOpenModalityRef.current === 'keyboard') {
      focusableElements()[1]?.focus();
    }

    const handleNavbarKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setExpanded(false);
        navbarToggleRef.current?.focus();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusables = focusableElements();
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleNavbarKeydown);
    return () => {
      document.removeEventListener('keydown', handleNavbarKeydown);
      pageMain?.removeAttribute('inert');
      if (previousMainAriaHidden === null) {
        pageMain?.removeAttribute('aria-hidden');
      } else {
        pageMain?.setAttribute('aria-hidden', previousMainAriaHidden);
      }
    };
  }, [expanded, isNavbarCollapsedView]);

  useEffect(() => {
    if (!isNavbarCollapsedView || !expanded) {
      return undefined;
    }

    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, [expanded, isNavbarCollapsedView]);

  useEffect(() => {
    if (!checkoutOverlayBackground) {
      return undefined;
    }

    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalBodyPosition = document.body.style.position;
    const originalBodyTop = document.body.style.top;
    const originalBodyLeft = document.body.style.left;
    const originalBodyRight = document.body.style.right;
    const originalBodyWidth = document.body.style.width;
    const lockedScrollY = window.scrollY;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.position = originalBodyPosition;
      document.body.style.top = originalBodyTop;
      document.body.style.left = originalBodyLeft;
      document.body.style.right = originalBodyRight;
      document.body.style.width = originalBodyWidth;
      window.scrollTo(0, lockedScrollY);
    };
  }, [checkoutOverlayBackground]);

  useEffect(() => {
    if (!checkoutOverlayBackground) {
      return undefined;
    }

    const dialog = checkoutDialogRef.current;
    if (!dialog) {
      return undefined;
    }

    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const overlay = dialog.closest<HTMLElement>('.checkout-route-overlay');
    const appRoot = overlay?.parentElement;
    const backgroundElements = Array.from(appRoot?.children || [])
      .filter(
        (element): element is HTMLElement => element instanceof HTMLElement && element !== overlay,
      )
      .map((element) => ({ element, wasInert: element.hasAttribute('inert') }));
    backgroundElements.forEach(({ element }) => element.setAttribute('inert', ''));

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    const visibleFocusableElements = () =>
      Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector)).filter((element) => {
        const style = window.getComputedStyle(element);
        return (
          !element.hidden &&
          element.getAttribute('aria-hidden') !== 'true' &&
          style.display !== 'none' &&
          style.visibility !== 'hidden'
        );
      });

    const isCheckoutDialogTopmost = () => {
      const modalDialogs = Array.from(
        document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]'),
      ).filter((element) => element.isConnected && element.getAttribute('aria-hidden') !== 'true');
      return modalDialogs[modalDialogs.length - 1] === dialog;
    };

    const focusFrame = window.requestAnimationFrame(() => {
      dialog.focus({ preventScroll: true });
    });

    const handleDialogKeyDown = (event: KeyboardEvent) => {
      if (!isCheckoutDialogTopmost()) {
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        closeCheckoutOverlay();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements = visibleFocusableElements();
      if (!focusableElements.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleDialogKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleDialogKeyDown);
      backgroundElements.forEach(({ element, wasInert }) => {
        if (!wasInert) {
          element.removeAttribute('inert');
        }
      });
      if (previousFocus?.isConnected) {
        previousFocus.focus();
      }
    };
  }, [checkoutOverlayBackground, closeCheckoutOverlay]);

  useEffect(() => {
    const handleCheckoutLinkClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.altKey ||
        event.ctrlKey ||
        event.shiftKey ||
        isCheckoutOverlayPath(location.pathname)
      ) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const anchor = target.closest('a[href]');
      if (
        !(anchor instanceof HTMLAnchorElement) ||
        (anchor.target && anchor.target !== '_self') ||
        anchor.hasAttribute('download')
      ) {
        return;
      }

      let checkoutUrl: URL;
      try {
        checkoutUrl = new URL(anchor.href, window.location.origin);
      } catch {
        return;
      }

      if (
        checkoutUrl.origin !== window.location.origin ||
        !isCheckoutOverlayPath(checkoutUrl.pathname)
      ) {
        return;
      }

      event.preventDefault();
      setExpanded(false);
      navigateToCheckoutOverlay(navigate, toInternalPath(checkoutUrl), location);
    };

    document.addEventListener('click', handleCheckoutLinkClick, true);
    return () => document.removeEventListener('click', handleCheckoutLinkClick, true);
  }, [location, navigate]);

  const navigateWithoutReload =
    (to: string): React.MouseEventHandler<HTMLElement> =>
    (event) => {
      closeNavbar();

      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.altKey ||
        event.ctrlKey ||
        event.shiftKey
      ) {
        return;
      }

      event.preventDefault();
      navigate(to);
    };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const surveyCompleted =
      params.get('survey_completed') === 'true' || params.get('attribution_completed') === 'true';
    if (surveyCompleted && isAuthenticated) {
      refreshProfile();
    }
  }, [location.search, isAuthenticated, refreshProfile]);

  const attributionGatePath = useMemo(() => {
    if (!isAuthenticated || authLoading || profileLoading || !resolvedUserProfile) {
      return null;
    }
    if (hasProfileAttribution(resolvedUserProfile)) {
      return null;
    }
    const authenticatedUserId = resolvedUserProfile.id || user?.id;
    if (!hasActivePostSignupJourney(authenticatedUserId)) {
      return null;
    }

    // Only authentication transition pages and the attribution page itself may
    // bypass this gate. Sales, checkout, prompt, marketplace, account, and all
    // future product routes are deliberately covered by the same invariant.
    const attributionExemptPaths = [
      '/signup-survey',
      '/post-signup',
      '/login',
      '/set-password',
      '/auto-login',
      '/auto-login-sent',
      '/stripe',
    ];
    if (attributionExemptPaths.some((path) => location.pathname.startsWith(path))) {
      return null;
    }

    const currentPath = `${location.pathname}${location.search}${location.hash}` || '/';
    const returnTo = getActivePostSignupDestination(authenticatedUserId, currentPath);
    const params = new URLSearchParams({
      forced_attribution: 'true',
      source_flow: 'required_post_signup_attribution',
      redirectTo: returnTo,
    });
    return `/signup-survey?${params.toString()}`;
  }, [
    isAuthenticated,
    authLoading,
    profileLoading,
    resolvedUserProfile,
    user?.id,
    location.pathname,
    location.search,
    location.hash,
  ]);

  useEffect(() => {
    if (attributionGatePath) {
      navigate(attributionGatePath, { replace: true });
    }
  }, [attributionGatePath, navigate]);

  const usesCompactWorkspaceShell = [
    '/account-settings',
    '/agents',
    '/my-strategies',
    '/my-backtests',
    '/my-bots',
    '/brokerage-accounts',
    '/bot/',
    '/dashboard',
  ].some((path) => visiblePathname.startsWith(path));

  const aiStrategyNavLink = (
    <Nav.Link
      key="ai-strategy"
      href="/ai-agent"
      onMouseEnter={() => loadAiAgent().catch(() => undefined)}
      onFocus={() => loadAiAgent().catch(() => undefined)}
      onClick={(e) => {
        e.preventDefault();
        closeNavbar();
        handleAIAgentClick();
      }}
      className={cn(
        'nav-action-primary nav-action-primary--redesign',
        visiblePathname === '/ai-agent' && 'active',
      )}
    >
      <AgentIcon className="nav-menu-link-icon" aria-hidden="true" />
      <span>AI Agent</span>
    </Nav.Link>
  );

  const marketplaceNavLink = (
    <Nav.Link
      key="marketplace"
      as={NavLink}
      to="/marketplace"
      className={cn(
        'nav-action-marketplace nav-action-marketplace--redesign',
        visiblePathname === '/marketplace' && 'active',
      )}
      onClick={closeNavbar}
    >
      <MarketplaceIcon className="nav-menu-link-icon" aria-hidden="true" />
      <span>Marketplace</span>
    </Nav.Link>
  );

  // Public funnel pages must not be held behind Auth0 startup. Auth still
  // hydrates in the background; these pages can render without it.
  const isAuthOptionalPublicRoute =
    location.pathname === '/' ||
    location.pathname.startsWith('/sales') ||
    location.pathname === '/lp/google-search' ||
    location.pathname.startsWith('/ai-bot-builder-bootcamp') ||
    location.pathname === '/prompt-guide' ||
    location.pathname === '/marketplace' ||
    location.pathname.startsWith('/marketplace/strategy/') ||
    location.pathname.startsWith('/marketplace/account/') ||
    location.pathname === '/sales-marketplace' ||
    location.pathname.startsWith('/case-studies') ||
    location.pathname === '/sample-complex-backtest' ||
    location.pathname === '/ai-workflow-audit' ||
    location.pathname.startsWith('/feedback/');

  // Add ready state. Protected routes handle their own auth loading; the
  // app shell should stay visible while Auth0 hydrates.
  const [isReady, setIsReady] = useState(true);
  // Track UTM parameters on every page load or route change.
  useEffect(() => {
    const trackUtmForRoute = () => {
      try {
        const utmData = trackUtmParameters();

        // Log detailed info when UTM parameters are found
        if (utmData) {
          // UTM data tracked successfully

          // If we have UTM data and a user is logged in, we could
          // update the user's attribution data in your backend
          if (isAuthenticated && user) {
            // Optional: Send UTM data to backend for this user
          }
        }
      } catch (error) {
        console.error('Error in UTM tracking:', error);
        // Non-blocking - don't throw errors from here
      }
    };

    trackUtmForRoute();
  }, [location.search, location.pathname, isAuthenticated, user]);

  // 1. Page View Tracking
  useEffect(() => {
    // FIXED: Get current UTM data for each page view
    const utmData = trackUtmParameters();

    // FIXED: Proper page tracking data
    const pageData = {
      event: ANALYTICS_EVENTS.PAGE_VIEW,
      page_location: window.location.href,
      page_path: location.pathname, // FIXED: Use current path, not referrer
      page_title: document.title,
      referrer: document.referrer,
    };
    if (utmData) {
      Object.assign(pageData, {
        utm_source: utmData.utm_source,
        utm_medium: utmData.utm_medium,
        utm_campaign: utmData.utm_campaign,
        utm_term: utmData.utm_term,
        utm_content: utmData.utm_content,
      });
    }

    // CRITICAL: Fire specific events for key pages
    if (location.pathname === '/upgrade-to-pro') {
      pushEvent({
        ...pageData,
        event: ANALYTICS_EVENTS.UPGRADE_TO_PRO_PAGE_VIEW,
        source: location.pathname,
        search_params: location.search,
      });
    } else if (location.pathname === '/set-password') {
      pushEvent({
        ...pageData,
        event: ANALYTICS_EVENTS.SET_PASSWORD_PAGE_VIEW,
        source: location.pathname,
        search_params: location.search,
      });
    } else if (location.pathname === '/sales') {
      const salesPageData = {
        ...pageData,
        event: ANALYTICS_EVENTS.SALES_PAGE_VIEW,
        source: location.pathname,
        search_params: location.search,
      };
      if (utmData) {
        Object.assign(salesPageData, {
          utm_source: utmData.utm_source,
          utm_medium: utmData.utm_medium,
          utm_campaign: utmData.utm_campaign,
          utm_term: utmData.utm_term,
          utm_content: utmData.utm_content,
        });
      }
      pushEvent(salesPageData);
    }

    // Always fire the general page view too
    pushEvent(pageData);
  }, [location.pathname, location.search]); // Added location.search to dependencies

  // 2. Auth State Tracking and User Identity
  useEffect(() => {
    // Skip if still loading
    if (authLoading) return;

    // Track authenticated state and update user identity across platforms
    if (isAuthenticated && user) {
      const analyticsUserId = getCanonicalAnalyticsUserId(user);
      const loginMethod = getAnalyticsLoginMethod(user.sub);
      pushEvent({
        event: ANALYTICS_EVENTS.USER_AUTHENTICATED,
        userId: analyticsUserId,
        userEmail: user.email,
        loginMethod,
        source: location.pathname,
      });

      // Set basic user identity (will be enhanced with payment status below)
      identifyUser({
        userId: analyticsUserId,
        userEmail: user.email,
        isAuthenticated: true,
      });
    } else if (!isAuthenticated) {
      // Update for anonymous users
      identifyUser({
        isAuthenticated: false,
      });
    }
  }, [authLoading, isAuthenticated, user, location.pathname]); // FIXED: Removed unstable getAccessTokenSilently, added missing location.pathname

  // 2b. 🆕 COMPREHENSIVE USER IDENTITY WITH PAYMENT STATUS - Enhanced Hotjar Integration
  useEffect(() => {
    // Only run when we have complete auth and payment data
    if (authLoading || paymentLoading) return;

    if (isAuthenticated && user) {
      const analyticsUserId = getCanonicalAnalyticsUserId(user);
      const loginMethod = getAnalyticsLoginMethod(user.sub);

      // Determine plan type based on payment status
      const planType = isPayingCustomer ? 'paid' : 'free';
      const subscriptionStatus = isPayingCustomer ? 'active' : 'free';

      // Check if this is the first time we're identifying this user in this session
      const sessionKey = `user_identified_${analyticsUserId}`;
      const isFirstTimeIdentification = !safeGetStorageItem('sessionStorage', sessionKey);

      if (isFirstTimeIdentification) {
        // Mark this user as identified in this session
        safeSetStorageItem('sessionStorage', sessionKey, 'true');
      }

      // Enhanced user identity with comprehensive data for Hotjar
      identifyUser({
        userId: analyticsUserId,
        userEmail: user.email,
        name: user.name || user.given_name || user.nickname,
        isAuthenticated: true,
        isPaying: isPayingCustomer,
        planType: planType,
        subscriptionStatus: subscriptionStatus,
        loginMethod: loginMethod,
        signupDate: user.created_at,
        isFirstTimeIdentification: isFirstTimeIdentification,
      });

      // Enhanced user identity set with Hotjar data
    }
  }, [authLoading, paymentLoading, isAuthenticated, user, isPayingCustomer]);

  // Handle seamless auto-login token from purchase flow (Deprecated - now handled by UnifiedAuthContext)
  /**
   * AUTO-LOGIN TOKEN HANDLING (DEPRECATED)
   *
   * This logic has been moved to UnifiedAuthContext for better separation of concerns.
   * The unified auth system automatically:
   * 1. Detects autoLoginToken in URL params
   * 2. Validates custom JWT token structure and expiry
   * 3. Sets up authentication state immediately (no Auth0 redirect needed)
   * 4. Removes token from URL for security
   * 5. User is instantly logged in and ready for API calls
   *
   * This section is commented out to avoid conflicts with the new system.
   */
  /*
  useEffect(() => {
    // Legacy auto-login handling - now done in UnifiedAuthContext
  }, []);
  */

  // 3. Hotjar Initialization - Skip on localhost/development
  useEffect(() => {
    // Check if we're running on localhost or local development
    const hostname = window.location.hostname;
    const isLocalhost =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0' ||
      hostname.endsWith('.local') ||
      hostname.includes('localhost') ||
      process.env.NODE_ENV === 'development';

    if (isLocalhost) {
      console.debug('[Hotjar] Skipping Hotjar initialization on localhost/development environment');
      return;
    }

    const hotjarId = Number(process.env.REACT_APP_HOTJAR_ID);
    const hotjarVersion = Number(process.env.REACT_APP_HOTJAR_VERSION);
    if (!Number.isFinite(hotjarId) || !Number.isFinite(hotjarVersion)) {
      console.warn('[Hotjar] Missing or invalid Hotjar environment variables');
      return;
    }

    // Only initialize Hotjar in production environments
    console.debug('[Hotjar] Initializing Hotjar for production environment');
    initHotjar(hotjarId, hotjarVersion, false, myCustomLogger);
  }, [initHotjar]);

  // 4. User Creation
  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      const ensureUser = async () => {
        try {
          console.debug('App: Ensuring user creation for', user.email);

          // Check if user is using direct authentication
          const directAuthData = safeGetStorageItem('localStorage', 'directAuthUser');
          if (directAuthData) {
            console.debug('App: User is using direct authentication, skipping Auth0 user creation');
            return;
          }

          const token = await getApiToken().catch((error: Error) => {
            console.debug(
              'Token error during ensureUser, will be handled on next page load:',
              error,
            );
            return null;
          });

          // If token refresh is needed, just skip this request
          if (!token) {
            console.debug('App: Token refresh needed, skipping user creation for now');
            return;
          }

          await fetchWithTimeout(`${process.env.REACT_APP_API_URL}/auth/ensure-user`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: user?.email || '',
            }),
          });
          console.debug('App: User creation successful');
        } catch (err) {
          if (err instanceof Error && err.name === 'AbortError') {
            console.warn('App: User creation check timed out; continuing app render');
          } else {
            console.error('Error ensuring user creation:', err);
          }
        }
      };
      ensureUser();
    }
  }, [authLoading, isAuthenticated, user, getApiToken]);

  // 5. Onboarding Redirect Logic - DISABLED
  //
  // DISABLED 2026-01-16: The aggressive onboarding redirect was causing more harm than good:
  // - Redirecting users away from AI Agent after they just paid (breaking post-checkout flow)
  // - Constantly redirecting "recent users" (< 24 hours) on every login
  // - Intercepting users from payment flows when they should go to their destination
  // - Breaking conversation context preservation (handoff system)
  //
  // Users can still access onboarding manually at /onboarding if they want it.
  // The OnboardingChecklist component in the sidebar provides non-intrusive guidance.
  //
  // If we want to re-enable a gentler version later, consider:
  // - Only redirect on FIRST EVER login (not subsequent logins)
  // - Show a dismissible banner instead of forcing a redirect
  // - Never redirect from payment flows
  useEffect(() => {
    // NUCLEAR OPTION: Disable all onboarding redirects
    // Users should go where they intend to go, not be yanked to onboarding
    if (!authLoading && isAuthenticated && user) {
      console.debug(
        'App: Onboarding redirect is DISABLED - users go to their intended destination',
      );
      return; // Exit early - no onboarding redirect
    }

    /* DISABLED - Original onboarding redirect logic preserved for reference
    if (!authLoading && isAuthenticated && user) {
      const checkOnboardingStatus = async () => {
        try {
          // Skip onboarding check for certain pages
          const skipOnboardingPages = [
            '/onboarding',
            '/set-password',
            '/logout',
            '/login',
            '/weekly-workshop',
            '/live-algo-lab',
            '/live-algo-lab/watch',
            '/ai-bot-builder-bootcamp',
            '/ai-bot-builder',
            '/add-bot',
            '/help',
            '/terms',
            '/privacy',
            '/blog',
            '/backtest',
            '/deploy',
            '/my-bots',
            '/user_profile',
            '/profile',
            '/bot',
            // Sales funnel pages - users should complete the sales flow before onboarding
            '/sales',           // Main sales page (A/B test router)
            '/sales-control',   // Control variant
            '/sales05',         // Sales variant 05
            '/sales06',         // Sales variant 06
            '/sales-ai-prompt', // AI prompt page in sales funnel
            '/lp/google-search', // Dedicated Google Search paid LP
            // Checkout pages - users should complete checkout before onboarding
            '/checkout'         // All checkout pages (includes /checkout/starter-plan)
          ];
          const currentPath = location.pathname;
          
          if (skipOnboardingPages.some(path => currentPath.startsWith(path))) {
            console.debug("App: Skipping onboarding check for page:", currentPath);
            return;
          }

          // IMPORTANT: Only do onboarding redirect logic for root path or specific entry points
          // This prevents the annoying redirects when users are trying to navigate normally
          const entryPoints = ['/', '/home'];
          const isEntryPoint = entryPoints.includes(currentPath);

          // CRITICAL FIX: Also redirect from any page if user just completed payment/signup flow
          const urlParams = new URLSearchParams(location.search);
          const isFromPaymentFlow = urlParams.get('pro_upgrade_success') === 'true' ||
                                   urlParams.get('source_flow')?.includes('success') ||
                                   urlParams.get('survey_completed') === 'true' ||
                                   urlParams.get('attribution_completed') === 'true' ||
                                   urlParams.get('auto_login') === 'true' ||
                                   urlParams.get('autoLoginToken') !== null || // CRITICAL FIX: Detect auto-login from purchase
                                   sessionStorage.getItem('autoLoginActive') === 'true';

          if (!isEntryPoint && !isFromPaymentFlow) {
            console.debug("App: Not an entry point and not from payment flow, skipping onboarding redirect for:", currentPath);
            return;
          }

          if (isFromPaymentFlow) {
            console.debug("App: User is from payment/signup flow, will check onboarding regardless of current path:", currentPath);
          }

          console.debug("App: Starting onboarding check for", user.email, "on path:", currentPath, {
            isEntryPoint,
            isFromPaymentFlow,
            reason: isFromPaymentFlow ? 'payment/signup flow' : 'entry point'
          });
          console.debug("App: Auth state before token check:", {
            isAuthenticated,
            authLoading,
            userSub: user?.sub,
            userEmail: user?.email
          });

          let token;
          let headers = {};

          // Check if user is using direct authentication
          const directAuthData = localStorage.getItem('directAuthUser');
          if (directAuthData) {
            console.debug("App: User is using direct authentication");
            const userData = JSON.parse(directAuthData);
            if (userData.token) {
              token = userData.token;
              headers.Authorization = `Bearer ${token}`;
              console.debug("App: Using direct auth token for onboarding check");
            } else {
              console.debug("App: Direct auth data found but no token available");
              return;
            }
          } else {
            // Add more detailed token debugging
            console.debug("App: Attempting to get access token...");
            token = await getApiToken();
            headers.Authorization = `Bearer ${token}`;
            console.debug("App: Successfully obtained access token");
          }

          const response = await fetch(`${process.env.REACT_APP_API_URL}/users/profile`, {
            headers
          });

          if (!response.ok) {
            console.error("App: Failed to fetch profile for onboarding check:", response.status);
            return;
          }

          const data = await response.json();
          console.debug("App: Received profile data:", data);
          const profile = data.profile;

          console.debug("App: Profile data for onboarding check:", {
            hasSetPassword: profile.hasSetPassword,
            hasDismissedOnboarding: profile.hasDismissedOnboarding,
            hasDeployedBot: profile.hasDeployedBot,
            email: user.email,
            currentPath: currentPath
          });

          // CRITICAL FIX: If user is authenticated, they MUST have set password already
          // Auth0 doesn't allow login without password, so this check was wrong
          // Also applies to direct auth users who successfully logged in
          if (isAuthenticated && !profile.hasSetPassword) {
            console.debug("App: User is authenticated but hasSetPassword is false. This is a data inconsistency - fixing backend state...");
            console.debug("App: User authentication details:", {
              isAuth0: !!user?.sub,
              isDirectAuth: !!directAuthData,
              userEmail: user?.email || 'unknown',
              profileEmail: profile.email
            });
            
            // Update backend to reflect reality - user is clearly authenticated so password is set
            try {
              const response = await fetch(`${process.env.REACT_APP_API_URL}/users/password-status`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  ...headers
                }
              });
              
              if (response.ok) {
                console.debug("App: Successfully updated password status in backend");
                // Update local profile state to reflect the change
                profile.hasSetPassword = true;
              } else {
                console.warn("App: Failed to update password status - response not ok:", response.status);
              }
            } catch (error) {
              console.warn("App: Failed to update password status due to error, but continuing:", error);
            }
          }

          // IMPROVED: Only redirect to onboarding if user hasn't dismissed it AND hasn't completed ALL onboarding tasks
          // Check if ALL onboarding tasks are complete
          const allTasksComplete = profile.hasSetPassword && 
                                 profile.hasDeployedBot && 
                                 profile.hasCreatedStrategy && 
                                 profile.hasRunBacktest && 
                                 profile.hasBookedCall && 
                                 profile.hasWatchedVideo;
          
          // Special case: if we're on onboarding page already, clear the session flag to allow proper logic
          if (currentPath === '/onboarding') {
            sessionStorage.removeItem('onboardingRedirectDone');
          }
          
          // FIXED: Improved auto-login detection and onboarding redirect logic
          const hasRedirectedThisSession = sessionStorage.getItem('onboardingRedirectDone');
          const currentUrlParams = new URLSearchParams(location.search);
          const isComingFromAutoLogin = currentUrlParams.get('pro_upgrade_success') === 'true' || 
                                        currentUrlParams.get('source_flow')?.includes('success') ||
                                        currentUrlParams.get('survey_completed') === 'true' ||
                                        currentUrlParams.get('attribution_completed') === 'true' ||
                                        currentUrlParams.get('auto_login') === 'true' ||
                                        currentUrlParams.get('autoLoginToken') !== null; // CRITICAL FIX: Also detect autoLoginToken
          
          // Check if user was recently created (within last 24 hours) - likely from signup flow
          const isRecentUser = profile.createdAt && 
                              (new Date() - new Date(profile.createdAt)) < (24 * 60 * 60 * 1000);
          
          // CRITICAL FIX: Detect if this is a fresh Auth0 login vs normal navigation
          // Only redirect to onboarding for fresh Auth0 logins, not auto-logins or normal navigation
          const isFreshAuth0Login = !isComingFromAutoLogin && !hasRedirectedThisSession && isEntryPoint;
          
          console.debug("App: Auto-login and recent user detection:", {
            isComingFromAutoLogin,
            isRecentUser,
            hasRedirectedThisSession,
            isFreshAuth0Login,
            urlParams: Object.fromEntries(currentUrlParams.entries()),
            profileCreatedAt: profile.createdAt
          });
          
          // FIXED: No longer need redirectTo handling since we use direct navigation
          // Auto-login now takes users directly to their intended destination
          
          // CRITICAL FIX: Define paths that should NOT be redirected to onboarding
          // These are part of the post-purchase flow and should be completed before onboarding
          const protectedPaths = ['/upgrade-to-pro', '/signup-survey'];
          const currentLocation = location.pathname;
          const isOnProtectedPath = protectedPaths.includes(currentLocation);
          
          // CRITICAL FIX: If user hasn't dismissed onboarding AND hasn't completed tasks, redirect to onboarding
          // BUT respect the intended post-purchase flow by not redirecting from protected paths
          if (!profile.hasDismissedOnboarding && !allTasksComplete && !isOnProtectedPath) {
            // FIXED: Only redirect in specific scenarios - NOT for normal navigation
            // 1. User from payment/signup flow should always be redirected
            // 2. Recent users should be redirected
            // 3. Fresh Auth0 login (not auto-login, not normal navigation) with incomplete tasks
            const shouldForceRedirect = isFromPaymentFlow || isRecentUser || isFreshAuth0Login;

            // CRITICAL: Check if user has a pending handoff (e.g., typed prompt on homepage while logged out)
            // If handoff exists, skip onboarding and let user proceed to /ai-agent
            // The handoff should take priority over onboarding
            const pendingHandoff = getAgentHandoff();
            if (pendingHandoff) {
              console.debug("App: User has pending handoff, skipping onboarding redirect to preserve user flow", {
                handoffSource: pendingHandoff.source,
                hasPrompt: !!pendingHandoff.prompt,
                currentPath: currentLocation
              });
              // Navigate to AI Agent to process the handoff
              navigate('/ai-agent', { replace: true });
              return;
            }

            if (shouldForceRedirect) {
              console.debug("App: User needs onboarding (tasks incomplete), redirecting to onboarding page", {
                isFromPaymentFlow,
                isRecentUser,
                isEntryPoint,
                isFreshAuth0Login,
                isComingFromAutoLogin,
                allTasksComplete,
                hasDismissedOnboarding: profile.hasDismissedOnboarding,
                shouldForceRedirect,
                currentPath: currentLocation,
                isOnProtectedPath,
                reason: isFromPaymentFlow ? 'payment/signup flow' : isRecentUser ? 'recent user' : 'fresh Auth0 login with incomplete tasks'
              });
              
              // Mark that we've redirected this session to avoid future redirects during normal navigation
              sessionStorage.setItem('onboardingRedirectDone', 'true');
              
              // CRITICAL FIX: Use replace to prevent homepage flicker
              navigate('/onboarding', { replace: true });
              return;
            } else {
              console.debug("App: User needs onboarding but redirect conditions not met - allowing normal navigation", {
                isFromPaymentFlow,
                isRecentUser,
                isEntryPoint,
                isFreshAuth0Login,
                isComingFromAutoLogin,
                allTasksComplete,
                shouldForceRedirect,
                currentPath: currentLocation,
                isOnProtectedPath,
                reason: 'Normal navigation - not forcing onboarding redirect'
              });
            }
          } else if (isOnProtectedPath) {
            console.debug("App: User is on protected path - allowing access before onboarding", {
              currentPath: currentLocation,
              allTasksComplete,
              hasDismissedOnboarding: profile.hasDismissedOnboarding
            });
          }

          console.debug("App: User has completed all onboarding tasks, dismissed it, or already redirected this session");
        } catch (err) {
          console.error("App: Error checking onboarding status:", err);
          console.error("App: Error stack:", err.stack);
        }
      };
      
      // Add a small delay to ensure Auth0 state is fully settled
      const timeoutId = setTimeout(() => {
        checkOnboardingStatus();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
    END OF DISABLED ONBOARDING LOGIC */
  }, [authLoading, isAuthenticated, user, navigate, location.pathname]); // FIXED: Removed unstable getAccessTokenSilently

  // Add explicit debug logging for PostHog
  useEffect(() => {
    // Log PostHog initialization status
    console.debug('PostHog initialization status:', {
      key: process.env.REACT_APP_PUBLIC_POSTHOG_KEY ? 'Configured' : 'Missing',
      host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST || 'Default',
    });
  }, []);

  // 5. Initialize Analytics (PostHog) - Remove any feature hardcoding
  useEffect(() => {
    if (authLoading) {
      return;
    }

    // Formal browser qualification must not depend on third-party analytics
    // availability. Dedicated conversion tests opt in before application code
    // runs; normal users retain the production analytics path.
    if (window.navigator.webdriver && window.__BOTSPOT_PLAYWRIGHT_EXTERNAL_ANALYTICS__ !== true) {
      return;
    }

    // Initialize analytics with user attributes if available
    const userAttributes =
      isAuthenticated && user
        ? {
            user_id: getCanonicalAnalyticsUserId(user),
            email: user.email,
            is_authenticated: true,
            is_paying_customer: isPayingCustomer,
          }
        : {
            is_authenticated: false,
          };

    // Initialize PostHog and other analytics providers
    initAnalytics(userAttributes);

    // ADDED: Initialize UTM debugging utilities
    initDebugging();

    console.debug('[App] Analytics initialized with PostHog');
  }, [authLoading, isAuthenticated, isPayingCustomer, user]);

  // Make app ready after authentication is checked. Public marketplace pages
  // are allowed through even while Auth0 is still hydrating so a bad/slow auth
  // session cannot strand users on "Loading website...".
  useEffect(() => {
    if (!authLoading || isAuthOptionalPublicRoute) {
      setIsReady(true);
    }
  }, [authLoading, isAuthOptionalPublicRoute]);

  const AuthOverlayBackgroundRoutes = ({
    backgroundLocation,
    fallback,
  }: {
    backgroundLocation: RouterLocation;
    fallback: React.ReactElement;
  }) => {
    const pathname = backgroundLocation.pathname;
    let page = fallback;

    const backgroundActionAuthorizationId = getActionAuthorizationIdFromPath(pathname);
    if (backgroundActionAuthorizationId) {
      page = <ActionAuthorizationBackdropPage actionIdOverride={backgroundActionAuthorizationId} />;
    } else if (pathname === '/') {
      page = <ControlRoomHome suppressSeo />;
    } else if (pathname === '/home-clarity') {
      page = <ClarityHome suppressSeo />;
    } else if (pathname === '/challenges-clarity' || pathname.startsWith('/challenges-clarity/')) {
      page = <ChallengeClarityReview />;
    } else if (pathname === '/courses-clarity' || pathname.startsWith('/courses-clarity/')) {
      page = <CourseClarityReview />;
    } else if (pathname === '/home-lovable') {
      page = <HomeLovable suppressSeo />;
    } else if (pathname === '/sales') {
      page = <SalesPage07 signupVariant="full_form" />;
    } else if (pathname === '/sales-control') {
      page = <SalesPage />;
    } else if (pathname === '/sales05') {
      page = <SalesPage05 />;
    } else if (pathname === '/sales06') {
      page = <SalesPage06 />;
    } else if (pathname === '/sales07') {
      page = <SalesPage07 />;
    } else if (pathname === '/sales08') {
      page = <SalesPage08 />;
    } else if (pathname === '/lp/google-search') {
      page = <GoogleSearchLandingPage />;
    } else if (pathname === '/home-control' || pathname === '/home') {
      page = <Home />;
    } else if (pathname === '/home-trust-strip') {
      page = <HomeWithTrustStrip />;
    } else if (pathname === '/home-old') {
      page = <HomeWithABTest />;
    } else if (pathname === '/marketplace') {
      page = <MarketplacePage />;
    } else if (pathname === '/terms') {
      page = <Terms />;
    } else if (pathname === '/privacy') {
      page = <Privacy />;
    } else if (pathname === '/pricing') {
      page = <PricingPage />;
    } else if (pathname === '/agents') {
      page = <AgentsPage />;
    } else if (pathname === '/enterprise') {
      page = <EnterprisePage />;
    } else if (pathname === '/ai-workflow-audit') {
      page = <FamilyOfficeAiAuditPage />;
    } else if (pathname === '/case-studies') {
      page = (
        <EducationDesignExperiment>
          <SampleComplexBacktestPage />
        </EducationDesignExperiment>
      );
    } else if (pathname === '/investor') {
      page = <InvestorPage />;
    } else if (pathname === '/prompt-guide') {
      page = <PromptGuide />;
    } else if (pathname === '/weekly-workshop') {
      page = <WeeklyWorkshop />;
    } else if (pathname === '/live-algo-lab') {
      page = (
        <EducationDesignExperiment>
          <LiveAlgoLabSalesPage />
        </EducationDesignExperiment>
      );
    } else if (pathname === '/live-algo-lab/watch') {
      page = (
        <EducationDesignExperiment>
          <LiveAlgoLabWatchPage />
        </EducationDesignExperiment>
      );
    } else if (pathname === '/courses/ai-trading-bootcamp') {
      page = (
        <EducationDesignExperiment>
          <CourseNameExperimentPage />
        </EducationDesignExperiment>
      );
    } else if (pathname === '/courses/ai-trading-bootcamp/self-paced') {
      page = (
        <EducationDesignExperiment>
          <CourseSelfPacedPage />
        </EducationDesignExperiment>
      );
    } else if (pathname === '/challenges') {
      page = (
        <EducationDesignExperiment>
          <NextChallengePage />
        </EducationDesignExperiment>
      );
    } else if (pathname === '/hackathon') {
      page = (
        <EducationDesignExperiment>
          <Contest />
        </EducationDesignExperiment>
      );
    } else if (pathname === '/hackathon/register') {
      page = (
        <EducationDesignExperiment>
          <ContestRegister />
        </EducationDesignExperiment>
      );
    } else if (pathname === '/blog') {
      page = (
        <EducationDesignExperiment>
          <BlogIndex />
        </EducationDesignExperiment>
      );
    } else if (pathname === '/blog/using-ai-to-make-ai-profitable-swing-trading-bot-in-seconds') {
      page = (
        <EducationDesignExperiment>
          <UsingAIToMakeAProfitableSwingTradingBotInSeconds />
        </EducationDesignExperiment>
      );
    } else if (pathname === '/help') {
      page = <Help />;
    }

    return <Suspense fallback={<Loading />}>{page}</Suspense>;
  };

  const LoginRouteOverlay = () => {
    const loginRouteLocation = useLocation();
    const loginRouteParams = useMemo(
      () => new URLSearchParams(loginRouteLocation.search),
      [loginRouteLocation.search],
    );
    const requestedLoginRouteReturnTo =
      loginRouteParams.get('returnTo') || loginRouteParams.get('successUrl');
    const loginRouteReturnTo = useMemo(
      () => normalizeSalesAuthReturnTo(requestedLoginRouteReturnTo || '/dashboard'),
      [requestedLoginRouteReturnTo],
    );
    const loginRouteNewSignupReturnTo = useMemo(
      () => normalizeSalesAuthReturnTo(requestedLoginRouteReturnTo || '/sales-choose'),
      [requestedLoginRouteReturnTo],
    );
    const loginRouteBackgroundLocation = useMemo(
      () =>
        getUsableAuthOverlayBackgroundLocation(
          getAuthOverlayBackgroundLocation(loginRouteLocation.state),
        ) ||
        getAuthOverlayBackgroundLocationFromPath(loginRouteReturnTo) ||
        getStoredAuthOverlayBackgroundLocation(),
      [loginRouteLocation.state, loginRouteReturnTo],
    );
    const loginRouteSource = loginRouteParams.get('source') || 'login_route';
    const loginRouteIntent = loginRouteParams.get('intent') === 'signup' ? 'signup' : 'login';
    const loginRouteHasFreshStepUpRequest =
      loginRouteParams.get('freshLogin') === '1' ||
      loginRouteParams.get('fresh_login') === '1' ||
      loginRouteParams.get('prompt') === 'login' ||
      loginRouteParams.get('max_age') === '0';
    const loginRouteRequiresFreshStepUp = loginRouteHasFreshStepUpRequest;
    const [loginRouteDialogOpen, setLoginRouteDialogOpen] = useState(true);
    const loginRouteAuthenticatedRef = useRef(false);
    const loginRouteFallback = <ControlRoomHome suppressSeo />;
    const shouldShowLoginRouteDialog = !isAuthenticated || loginRouteRequiresFreshStepUp;

    const handleLoginRouteOpenChange = (open: boolean) => {
      setLoginRouteDialogOpen(open);
      if (open || isAuthenticated || loginRouteAuthenticatedRef.current) {
        return;
      }

      const closeTarget = loginRouteBackgroundLocation
        ? toLocationPath(loginRouteBackgroundLocation)
        : '/';
      navigate(closeTarget, { replace: true });
    };

    if (isAuthenticated && !loginRouteRequiresFreshStepUp) {
      return <Navigate to={loginRouteReturnTo} replace />;
    }

    return (
      <>
        {loginRouteBackgroundLocation ? (
          <AuthOverlayBackgroundRoutes
            backgroundLocation={loginRouteBackgroundLocation}
            fallback={loginRouteFallback}
          />
        ) : (
          loginRouteFallback
        )}
        <AuthFirstDialog
          analyticsContext={{
            auth_surface: loginRouteRequiresFreshStepUp
              ? 'fresh_login_product_overview'
              : loginRouteIntent === 'signup'
                ? 'signup_product_overview'
                : 'login_product_overview',
            entry_source: loginRouteSource,
            source: loginRouteRequiresFreshStepUp
              ? loginRouteSource
              : loginRouteIntent === 'signup'
                ? 'signup_auth_first'
                : 'login_auth_first',
          }}
          authSourceOverride={loginRouteRequiresFreshStepUp ? loginRouteSource : undefined}
          entryMode={loginRouteIntent}
          forceFreshLogin={loginRouteRequiresFreshStepUp}
          newSignupReturnTo={loginRouteNewSignupReturnTo}
          onAuthenticated={() => {
            loginRouteAuthenticatedRef.current = true;
          }}
          onOpenChange={handleLoginRouteOpenChange}
          open={loginRouteDialogOpen && shouldShowLoginRouteDialog}
          returnTo={loginRouteReturnTo}
        />
      </>
    );
  };

  // Sales Page Signup Framing v5
  // Clean two-way test between email-only lead capture and auth-first account creation.
  // Keep the routed component type stable across AppContent rerenders. PostHog
  // analytics/feature-flag updates can rerender AppContent while the contact
  // form is advancing; recreating this component type would remount the whole
  // sales route and erase the in-progress attribution step.
  const SalesPageWithABTest = useMemo(
    () =>
      function SalesPageWithABTestComponent({ authenticated }: { authenticated: boolean }) {
        const location = useLocation();
        const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
        const [frozenSignupV4Variant, setFrozenSignupV4Variant] = useState<string | null>(null);
        const [authFirstDialogOpen, setAuthFirstDialogOpen] = useState(true);
        const [authFirstBackgroundLocation] = useState<RouterLocation>(() => {
          const explicitBackground = getUsableAuthOverlayBackgroundLocation(
            getAuthOverlayBackgroundLocation(location.state),
          );
          const storedBackground = getUsableAuthOverlayBackgroundLocation(
            getStoredAuthOverlayBackgroundLocation(),
          );
          const background = explicitBackground || storedBackground;

          return background?.pathname === '/sales'
            ? getDefaultAuthOverlayBackgroundLocation()
            : background || getDefaultAuthOverlayBackgroundLocation();
        });

        const experiment = ACTIVE_EXPERIMENTS.SALES_PAGE_SIGNUP_FRAMING_V5;
        const experimentName = experiment.name;
        const featureFlagKey = experiment.featureFlag || 'sales_page_signup_framing_v5_variant';
        const featureFlagContextProperty = `$feature_flag_${featureFlagKey}`;
        const storageKey = `${featureFlagKey}:frozen_variant`;
        const isSalesSignupV4ExperimentSurface =
          location.pathname === '/sales' && isBotSpotSalesExperimentHost();
        const rawSignupV4Override = searchParams.get('signupV5') || searchParams.get('signupV4');
        const normalizedSignupV4Override = isPreviewExperimentOverrideHost()
          ? normalizeSalesSignupV5Variant(rawSignupV4Override)
          : null;
        const fallbackSignupV4Variant = useMemo(
          () =>
            isSalesSignupV4ExperimentSurface
              ? getStableSalesSignupV5FallbackVariant()
              : 'email_only',
          [isSalesSignupV4ExperimentSurface],
        );
        const initialFlagSignupV4Variant = useMemo(
          () =>
            isSalesSignupV4ExperimentSurface
              ? getSynchronousSalesSignupV5FlagVariant(featureFlagKey)
              : null,
          [featureFlagKey, isSalesSignupV4ExperimentSurface],
        );
        const getInitialFrozenSignupV4Variant = () => {
          if (!isSalesSignupV4ExperimentSurface) {
            return null;
          }

          if (normalizedSignupV4Override) {
            return normalizedSignupV4Override;
          }

          try {
            const storedVariant = window.sessionStorage?.getItem(storageKey);
            return normalizeSalesSignupV5Variant(storedVariant);
          } catch (error) {
            console.warn('[Sales Signup Framing v5] Unable to read frozen variant:', error);
            return null;
          }
        };
        const initialFrozenSignupV4Variant = getInitialFrozenSignupV4Variant();
        const shouldSkipSignupV4FlagEvaluation =
          !isSalesSignupV4ExperimentSurface ||
          isValidSalesSignupV5Variant(initialFrozenSignupV4Variant);
        const shouldTrackFrozenSignupV4Exposure =
          isSalesSignupV4ExperimentSurface &&
          !signupV4ExposureResolutionRef.current.resolvedThisPageView &&
          shouldSkipSignupV4FlagEvaluation &&
          !normalizedSignupV4Override;

        const { variant: flagVariant, isLoading: isExperimentLoading } = useTypedABTest(
          experimentName,
          featureFlagKey,
          fallbackSignupV4Variant,
          {
            source: location.pathname,
            utm_source: searchParams.get('utm_source'),
            utm_medium: searchParams.get('utm_medium'),
            utm_campaign: searchParams.get('utm_campaign'),
            validVariants: SALES_SIGNUP_V5_ACCEPTED_FLAG_VARIANTS,
            normalizeControlExposure: true,
            skipFeatureFlagEvaluation: shouldSkipSignupV4FlagEvaluation,
            forcedVariant: isSalesSignupV4ExperimentSurface
              ? initialFrozenSignupV4Variant
              : 'email_only',
            forcedExposureValue:
              toSalesSignupV5PostHogVariant(initialFrozenSignupV4Variant) ||
              initialFrozenSignupV4Variant,
            trackForcedExposure: shouldTrackFrozenSignupV4Exposure,
            exposure_source: shouldTrackFrozenSignupV4Exposure
              ? 'sales_signup_v5_frozen_variant'
              : undefined,
            experiment_surface_enabled: isSalesSignupV4ExperimentSurface,
          },
        );

        useEffect(() => {
          if (!isSalesSignupV4ExperimentSurface) {
            setFrozenSignupV4Variant(null);
            return;
          }

          const normalizedOverride = isPreviewExperimentOverrideHost()
            ? normalizeSalesSignupV5Variant(rawSignupV4Override)
            : null;
          if (normalizedOverride) {
            signupV4ExposureResolutionRef.current.resolvedThisPageView = true;
            setFrozenSignupV4Variant(normalizedOverride);
            return;
          }

          if (isExperimentLoading) {
            return;
          }

          const storedVariant = (() => {
            try {
              return window.sessionStorage?.getItem(storageKey);
            } catch (error) {
              console.warn('[Sales Signup Framing v5] Unable to read frozen variant:', error);
              return null;
            }
          })();

          const normalizedStoredVariant = normalizeSalesSignupV5Variant(storedVariant);
          if (normalizedStoredVariant) {
            signupV4ExposureResolutionRef.current.resolvedThisPageView = true;
            setFrozenSignupV4Variant(normalizedStoredVariant);
            return;
          }

          const nextVariant = normalizeSalesSignupV5Variant(flagVariant) || fallbackSignupV4Variant;
          try {
            window.sessionStorage?.setItem(storageKey, nextVariant);
          } catch (error) {
            console.warn('[Sales Signup Framing v5] Unable to persist frozen variant:', error);
          }
          signupV4ExposureResolutionRef.current.resolvedThisPageView = true;
          setFrozenSignupV4Variant(nextVariant);
        }, [
          fallbackSignupV4Variant,
          flagVariant,
          isExperimentLoading,
          isSalesSignupV4ExperimentSurface,
          rawSignupV4Override,
          storageKey,
        ]);

        const variant =
          frozenSignupV4Variant ||
          initialFrozenSignupV4Variant ||
          initialFlagSignupV4Variant ||
          flagVariant;

        // Normalize variant - v5 control is the email-only route.
        const normalizedVariant =
          normalizeSalesSignupV5Variant(variant) ||
          normalizeSalesSignupV5Variant(fallbackSignupV4Variant) ||
          'email_only';
        const posthogVariant =
          toSalesSignupV5PostHogVariant(variant) ||
          toSalesSignupV5PostHogVariant(normalizedVariant) ||
          SALES_SIGNUP_V5_DEFAULT_VARIANT;

        const formVariant = normalizedVariant === 'email_only' ? 'email_only' : 'full_form';
        const framingVariant = normalizedVariant === 'auth_first' ? 'auth_first' : 'create_account';
        const designVariant = normalizedVariant === 'auth_first' ? 'login_menu' : 'sales07';
        const hasResolvedSignupV4Variant = hasResolvedSalesSignupV4Variant({
          initialFrozenVariant: initialFrozenSignupV4Variant || initialFlagSignupV4Variant,
          frozenVariant: frozenSignupV4Variant,
          isExperimentLoading: isSalesSignupV4ExperimentSurface ? isExperimentLoading : false,
        });

        // Set experiment context for downstream tracking (SalesAiPromptPage, checkout, etc.)
        const shouldAttachSignupV4ExperimentContext =
          isSalesSignupV4ExperimentSurface && !normalizedSignupV4Override;
        const experimentContext = useMemo(
          () =>
            shouldAttachSignupV4ExperimentContext
              ? {
                  [featureFlagContextProperty]: posthogVariant,
                  [`$feature/${featureFlagKey}`]: posthogVariant,
                  sales_page_signup_framing_variant: normalizedVariant,
                  sales_page_signup_framing_v5_variant: posthogVariant,
                  sales_page_signup_framing_v5_route_variant: normalizedVariant,
                  sales_page_form_variant: formVariant,
                  sales_page_framing_variant: framingVariant,
                  sales_page_design_variant: designVariant,
                  $feature_flag_sales_page_design_variant: designVariant,
                  experiment_variant: normalizedVariant,
                  experiment_name: experimentName,
                  feature_flag: featureFlagKey,
                }
              : {
                  sales_page_signup_framing_variant: normalizedVariant,
                  sales_page_form_variant: formVariant,
                  sales_page_framing_variant: framingVariant,
                  sales_page_design_variant: designVariant,
                  $feature_flag_sales_page_design_variant: designVariant,
                  experiment_variant: normalizedVariant,
                  sales_page_signup_framing_v5_override: Boolean(normalizedSignupV4Override),
                  sales_page_signup_framing_v5_experiment_surface: false,
                },
          [
            designVariant,
            experimentName,
            featureFlagContextProperty,
            featureFlagKey,
            formVariant,
            framingVariant,
            normalizedVariant,
            normalizedSignupV4Override,
            posthogVariant,
            shouldAttachSignupV4ExperimentContext,
          ],
        );
        const authFirstReturnMetadata = useMemo(
          () => ({
            [featureFlagContextProperty]: posthogVariant,
            [`$feature/${featureFlagKey}`]: posthogVariant,
            feature_flag: featureFlagKey,
            experiment_name: experimentName,
            experiment_variant: posthogVariant,
            sales_page_signup_framing_variant: normalizedVariant,
            sales_page_signup_framing_v5_experiment_name: experimentName,
            sales_page_signup_framing_v5_variant: posthogVariant,
            sales_page_signup_framing_v5_route_variant: normalizedVariant,
            sales_page_form_variant: formVariant,
            sales_page_framing_variant: framingVariant,
            sales_page_design_variant: designVariant,
          }),
          [
            designVariant,
            experimentName,
            featureFlagContextProperty,
            featureFlagKey,
            formVariant,
            framingVariant,
            normalizedVariant,
            posthogVariant,
          ],
        );
        const authFirstReturnTo = useMemo(
          () =>
            buildSalesAuthReturnToPath(
              getSalesAuthFirstReturnTo(searchParams.get('successUrl')),
              authFirstReturnMetadata,
            ),
          [authFirstReturnMetadata, searchParams],
        );
        const authFirstNewSignupReturnTo = useMemo(
          () =>
            buildSalesAuthReturnToPath(
              searchParams.get('successUrl') || '/sales-choose',
              authFirstReturnMetadata,
            ),
          [authFirstReturnMetadata, searchParams],
        );
        const authFirstAnalyticsContext = useMemo(
          () => ({
            ...experimentContext,
            ...authFirstReturnMetadata,
            sales_auth_first_presentation: 'overlay',
            sales_auth_first_return_to: authFirstReturnTo,
          }),
          [authFirstReturnMetadata, authFirstReturnTo, experimentContext],
        );
        const shouldRenderAuthFirstSalesPage = normalizedVariant === 'auth_first';

        window.__experimentContext = experimentContext;

        const salesSignupV4RenderExposureExtra = useMemo(
          () => ({
            sales_page_signup_framing_variant: normalizedVariant,
            sales_page_signup_framing_v5_route_variant: normalizedVariant,
            sales_page_form_variant: formVariant,
            sales_page_framing_variant: framingVariant,
            sales_page_design_variant: designVariant,
          }),
          [designVariant, formVariant, framingVariant, normalizedVariant],
        );
        useExperimentRenderExposure({
          shouldTrack: shouldAttachSignupV4ExperimentContext && hasResolvedSignupV4Variant,
          experimentName,
          featureFlagKey,
          posthogVariant,
          routeVariant: normalizedVariant,
          surface: 'sales_page_v5',
          path: location.pathname,
          exposureSource:
            initialFrozenSignupV4Variant || frozenSignupV4Variant
              ? 'sales_signup_v5_frozen_render'
              : 'sales_signup_v5_flag_render',
          extra: salesSignupV4RenderExposureExtra,
        });

        if (!hasResolvedSignupV4Variant) {
          return <Loading />;
        }

        console.debug('🎯 [Sales Signup Framing v5] Rendering sales variant', {
          variant: normalizedVariant,
          posthogVariant,
          formVariant,
          framingVariant,
          designVariant,
        });

        if (shouldRenderAuthFirstSalesPage) {
          if (authenticated) {
            return <Navigate to={authFirstReturnTo} replace />;
          }

          const handleAuthFirstDialogOpenChange = (open: boolean) => {
            setAuthFirstDialogOpen(open);
            if (open) {
              return;
            }

            navigate(toLocationPath(authFirstBackgroundLocation), {
              replace: true,
              state: authFirstBackgroundLocation.state || null,
            });
          };

          return (
            <>
              <AuthOverlayBackgroundRoutes
                backgroundLocation={authFirstBackgroundLocation}
                fallback={<ControlRoomHome suppressSeo />}
              />
              <AuthFirstDialog
                analyticsContext={authFirstAnalyticsContext}
                newSignupReturnTo={authFirstNewSignupReturnTo}
                onOpenChange={handleAuthFirstDialogOpenChange}
                open={authFirstDialogOpen}
                returnTo={authFirstReturnTo}
              />
            </>
          );
        }

        return (
          <SalesPage07
            signupVariant={normalizedVariant}
            experimentContext={
              shouldAttachSignupV4ExperimentContext ? experimentContext : undefined
            }
            trackSignupFramingExperiment={shouldAttachSignupV4ExperimentContext}
          />
        );
      },
    // The nested background renderer is intentionally captured with the
    // stable route type; depending on its per-render function identity would
    // recreate this component and reintroduce the form-state loss above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate],
  );

  // Homepage Trust Strip A/B Test Component
  const HomeWithABTest = () => {
    const location = useLocation();
    const experiment = ACTIVE_EXPERIMENTS.HOMEPAGE_TRUST_STRIP;

    const { variant, isLoading } = useTypedABTest(
      experiment.name,
      experiment.featureFlag || 'homepage-trust-strip-test',
      'control',
      {
        page: 'homepage',
        source: location.pathname,
        utm_source: new URLSearchParams(location.search).get('utm_source'),
        utm_medium: new URLSearchParams(location.search).get('utm_medium'),
        utm_campaign: new URLSearchParams(location.search).get('utm_campaign'),
      },
    );

    const resolvedVariant = isLoading ? 'control' : variant;
    const showTrustStripVariant =
      resolvedVariant !== null && resolvedVariant !== false && resolvedVariant !== 'control';
    const experimentContext = {
      $feature_flag_homepage_trust_strip: showTrustStripVariant ? resolvedVariant : 'control',
      experiment_variant: showTrustStripVariant ? resolvedVariant : 'control',
      experiment_name: experiment.name,
    };

    console.debug(`🎯 [Homepage A/B Test] Rendering:`, {
      variant: resolvedVariant,
      showTrustStripVariant,
      component: showTrustStripVariant ? 'HomeWithTrustStrip' : 'Home',
      experimentContext,
    });

    // Store experiment context globally for components to use
    window.__homepageExperimentContext = experimentContext;

    return showTrustStripVariant ? <HomeWithTrustStrip /> : <Home />;
  };

  // Removed handleAddBotClick - now using marketplace navigation

  const handleAIAgentClick = () => {
    // Enhanced debugging
    console.debug('AI Agent click - Auth state:', {
      isAuthenticated,
      email: user?.email,
      sub: user?.sub,
    });

    // Navigate to the protected AI Agent page while auth is resolving; ProtectedRoute
    // owns the final auth/paywall decision once hydration completes.
    if (isAuthenticated || authLoading) {
      console.debug('User is authenticated, navigating directly to AI Agent');
      navigate('/ai-agent', { replace: true });
      return;
    }

    // Only for non-authenticated users: redirect to sales
    console.debug('User is NOT authenticated, redirecting to sales');
    const targetPath = '/ai-agent';
    const successUrl = `${window.location.origin}${targetPath}`;
    navigate(`/sales?showLogin=1&successUrl=${encodeURIComponent(successUrl)}`, {
      state: buildAuthOverlayRouteState(location),
    });
  };

  // Improved login handler with explicit parameters and error handling
  const handleLogin = () => {
    try {
      pushEvent({
        event: ANALYTICS_EVENTS.LOGIN_CLICKED,
        method: 'auth_product_overlay',
        source: location.pathname,
      });

      navigate(headerLoginPath, {
        state: buildAuthOverlayRouteState(location),
      });
    } catch (error) {
      console.error('Login overlay error:', error);
      // Fallback to home page to avoid a broken state
      window.location.href = window.location.origin;
    }
  };

  const handleSignup = () => {
    try {
      pushEvent({
        event: ANALYTICS_EVENTS.LOGIN_CLICKED,
        method: 'auth_product_overlay',
        source: location.pathname,
        auth_intent: 'signup',
      });

      navigate(headerSignupPath, {
        state: buildAuthOverlayRouteState(location),
      });
    } catch (error) {
      console.error('Signup overlay error:', error);
      window.location.href = window.location.origin;
    }
  };

  useEffect(() => {
    // Legacy cleanup: Remove old direct auth data if present
    const directAuth = safeGetStorageItem('localStorage', 'directAuthUser');
    const autoLoginActive = safeGetStorageItem('sessionStorage', 'autoLoginActive');

    if (directAuth || autoLoginActive) {
      console.debug('[App] Cleaning up legacy direct auth data...');
      safeRemoveStorageItem('localStorage', 'directAuthUser');
      safeRemoveStorageItem('sessionStorage', 'autoLoginActive');
      safeRemoveStorageItem('localStorage', 'autoLoginUser');
    }
  }, []);

  // Handle temporary authentication cleanup
  useEffect(() => {
    // Clean up any legacy auto-login data when properly authenticated
    const autoLoginUser = safeGetStorageItem('localStorage', 'autoLoginUser');
    if (isAuthenticated && autoLoginUser) {
      console.debug('[App] Unified authentication complete, cleaning up legacy auto-login data');
      safeRemoveStorageItem('localStorage', 'autoLoginUser');
      window.tempAutoLoginActive = false;
    }
  }, [isAuthenticated]);

  const educationMenuGroups: NavMenuDropdownItem[][] = [
    [
      {
        key: 'ai-bootcamp',
        label: 'AI Bootcamp',
        iconClassName: 'fas fa-robot',
        to: aiBootcampEducationPath,
        active:
          location.pathname.startsWith('/ai-bot-builder-bootcamp') ||
          location.pathname.startsWith('/courses/ai-trading-bootcamp'),
        onClick: closeNavbar,
      },
      {
        key: 'live-algo-lab',
        label: 'Live Algo Lab',
        iconClassName: 'fas fa-calendar-alt',
        to: '/live-algo-lab',
        active:
          location.pathname === '/live-algo-lab' || location.pathname === '/live-algo-lab/watch',
        onClick: closeNavbar,
      },
      {
        key: 'challenges',
        label: 'Challenges',
        iconClassName: 'fas fa-flag-checkered',
        to: challengesEducationPath,
        active: location.pathname.startsWith('/challenges'),
        onClick: closeNavbar,
      },
      {
        key: 'hackathon',
        label: 'Trading Competition',
        iconClassName: 'fas fa-trophy',
        to: '/hackathon',
        active: location.pathname === '/hackathon',
        onClick: closeNavbar,
      },
    ],
    [
      {
        key: 'case-studies',
        label: 'Case Studies',
        iconClassName: 'fas fa-chart-line',
        to: '/case-studies',
        active: location.pathname.startsWith('/case-studies'),
        onClick: closeNavbar,
      },
      {
        key: 'blog',
        label: 'Blog',
        iconClassName: 'fas fa-newspaper',
        to: '/blog',
        active: location.pathname.startsWith('/blog'),
        onClick: closeNavbar,
      },
    ],
  ];
  const isEducationRouteActive = educationMenuGroups.some((group) =>
    group.some((item) => item.active),
  );
  const secondaryNavigationMenuItems: NavMenuDropdownItem[] = [
    {
      key: 'enterprise',
      label: 'Enterprise',
      iconClassName: 'fas fa-building',
      to: '/enterprise',
      active: location.pathname === '/enterprise',
      onClick: closeNavbar,
    },
    {
      key: 'pricing',
      label: 'Pricing',
      iconClassName: 'fas fa-credit-card',
      to: '/pricing',
      active: location.pathname === '/pricing',
      onClick: closeNavbar,
    },
    {
      key: 'affiliate',
      label: 'Affiliate',
      iconClassName: 'fas fa-handshake',
      to: '/affiliate',
      active: location.pathname === '/affiliate',
      onClick: closeNavbar,
    },
  ];

  const workspaceMenuItems: NavMenuDropdownItem[] = [
    {
      key: 'brokerage-accounts',
      label: 'Accounts',
      iconClassName: 'fas fa-building-columns',
      to: '/brokerage-accounts',
      active: location.pathname.startsWith('/brokerage-accounts'),
      onClick: navigateWithoutReload('/brokerage-accounts'),
    },
    {
      key: 'my-strategies',
      label: 'Strategies',
      iconClassName: 'fas fa-layer-group',
      to: '/my-strategies',
      active: location.pathname === '/my-strategies',
      onClick: navigateWithoutReload('/my-strategies'),
    },
    {
      key: 'my-backtests',
      label: 'Backtests',
      iconClassName: 'fas fa-chart-line',
      to: '/my-backtests',
      active: location.pathname === '/my-backtests',
      onClick: navigateWithoutReload('/my-backtests'),
    },
    {
      key: 'my-bots',
      label: 'Bots',
      iconClassName: 'fas fa-robot',
      to: '/my-bots',
      active: location.pathname === '/my-bots',
      onClick: navigateWithoutReload('/my-bots'),
    },
  ];

  const accountSettingsSectionMenuItems: NavMenuDropdownItem[] = [
    {
      key: 'profile',
      label: 'My Profile',
      iconClassName: 'fas fa-user',
      to: '/account-settings/profile',
      active: location.pathname === '/account-settings/profile',
      onClick: closeNavbar,
    },
    {
      key: 'appearance',
      label: 'Appearance',
      iconClassName: 'fas fa-palette',
      to: '/account-settings/appearance',
      active: location.pathname === '/account-settings/appearance',
      onClick: closeNavbar,
    },
    {
      key: 'api-keys',
      label: 'API Keys',
      iconClassName: 'fas fa-key',
      to: '/account-settings/api-keys',
      active: location.pathname === '/account-settings/api-keys',
      onClick: closeNavbar,
    },
    {
      key: 'subscriptions',
      label: 'Subscriptions & Billing',
      iconClassName: 'fas fa-credit-card',
      to: '/account-settings/subscriptions',
      active: location.pathname === '/account-settings/subscriptions',
      onClick: closeNavbar,
    },
    {
      key: 'usage',
      label: 'Usage',
      iconClassName: 'fas fa-chart-line',
      to: '/account-settings/usage',
      active: location.pathname === '/account-settings/usage',
      onClick: closeNavbar,
    },
    {
      key: 'connections',
      label: 'Third-party Connections',
      iconClassName: 'fas fa-plug',
      to: '/account-settings/connections',
      active: location.pathname === '/account-settings/connections',
      onClick: closeNavbar,
    },
    {
      key: 'saved-env-vars',
      label: 'Saved Variables',
      iconClassName: 'fas fa-lock',
      to: '/account-settings/saved-env-vars',
      active: location.pathname === '/account-settings/saved-env-vars',
      onClick: closeNavbar,
    },
    {
      key: 'broker-connections',
      label: 'Broker Connections',
      iconClassName: 'fas fa-building-columns',
      to: '/account-settings/broker-connections',
      active: location.pathname === '/account-settings/broker-connections',
      onClick: closeNavbar,
    },
    {
      key: 'notifications',
      label: 'Notifications',
      iconClassName: 'fas fa-bell',
      to: '/account-settings/notifications',
      active: location.pathname === '/account-settings/notifications',
      onClick: closeNavbar,
    },
  ];

  const accountLogoutMenuGroup: NavMenuDropdownItem[] = [
    {
      key: 'logout',
      label: 'Logout',
      iconClassName: 'fas fa-sign-out-alt',
      className: 'account-menu-logout',
      onClick: () => {
        closeNavbar();
        identifiedAnalyticsUserRef.current = null;
        customLogout();
      },
    },
  ];

  const accountOverviewMenuItems = accountSettingsSectionMenuItems.filter((item) =>
    ['profile', 'appearance', 'subscriptions', 'usage', 'notifications'].includes(item.key),
  );
  const accountToolsMenuItems = accountSettingsSectionMenuItems.filter((item) =>
    ['api-keys', 'connections', 'saved-env-vars', 'broker-connections'].includes(item.key),
  );
  const authenticatedAccountMenuGroups: NavMenuDropdownItem[][] = [
    workspaceMenuItems,
    accountOverviewMenuItems,
    accountToolsMenuItems,
    secondaryNavigationMenuItems,
    accountLogoutMenuGroup,
  ];
  const authenticatedAccountMenuGroupLabels = [
    'Workspace',
    'Account',
    'Tools and connections',
    'Company',
    undefined,
  ];
  // Render loading state after all hooks are declared
  if (!isReady) {
    return <Loading />;
  }
  if (attributionGatePath) {
    return <Loading />;
  }

  return (
    <div
      className={[
        'App',
        usesCompactWorkspaceShell ? 'App--compact-workspace' : '',
        isAiAgentPage ? 'App--ai-agent' : '',
        isDashboardPage ? 'App--dashboard' : '',
        isMarketplacePage ? 'App--marketplace' : '',
        shouldRenderAuthenticatedSidebar ? 'App--authenticated-sidebar' : '',
        'App--navbar-redesign',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* ReauthOverlay: Handles session expiry with error boundary for safety */}
      <ReauthOverlayErrorBoundary>
        <ReauthOverlay />
      </ReauthOverlayErrorBoundary>
      <AutoLoginHandler />

      {shouldRenderAuthenticatedSidebar && (
        <SidebarProvider>
          <AuthenticatedSidebar
            aiBootcampPath={aiBootcampEducationPath}
            challengesPath={challengesEducationPath}
            currentPath={location.pathname}
            logoSrc={botspotLogo}
            onLogout={() => {
              identifiedAnalyticsUserRef.current = null;
              customLogout();
            }}
            preloadAiAgent={() => {
              loadAiAgent().catch(() => undefined);
            }}
            userId={authenticatedNavigationUserId}
            userLabel={user?.name || user?.email || 'Account'}
          />
        </SidebarProvider>
      )}

      {/* Conditionally render the header */}
      {shouldRenderHeader && !shouldRenderAuthenticatedSidebar && (
        <header className="App-header">
          <Navbar
            variant={navbarRedesignTheme === 'light' ? 'light' : 'dark'}
            className={cn(
              'navbar-lumi navbar-lumi--redesign bsui-header-navbar bsui-scope fixed-top',
              isNavbarScrolled && 'navbar-lumi--scrolled',
            )}
            data-bs-theme={navbarRedesignTheme}
            expand="xl"
            expanded={expanded}
            onToggle={setExpanded}
          >
            <Container fluid>
              <Link
                className="navbar-brand"
                to={isAuthenticated ? '/dashboard' : '/'}
                onClick={closeNavbar}
              >
                {/*
                  Real transparent-PNG horizontal lockup: mascot icon badge +
                  "BotSpot" wordmark + "by LumiWealth" tagline, all in a
                  single RGBA PNG generated via nano-banana on a cyan
                  chroma-key background and processed to true alpha by
                  Brand Assets/BotSpot/process_brand_asset.py. Sits cleanly
                  on the navbar gradient with no background seam.
                */}
                <span className="botspot-logo-lockup">
                  <img
                    src={botspotLogo}
                    alt="BotSpot by LumiWealth"
                    className="botspot-logo-img botspot-logo-img--base"
                  />
                  <img
                    src={botspotLogo}
                    alt=""
                    aria-hidden="true"
                    className="botspot-logo-img botspot-logo-img--mascot"
                  />
                </span>
              </Link>

              <Nav className="desktop-menu desktop-menu--redesign nav-auth-menu--redesign nav-auth-menu--topbar ms-lg-auto">
                {isAuthenticated ? (
                  <>
                    {!isNavbarMobileView && (
                      <NavMenuDropdown
                        active={location.pathname.startsWith('/account-settings')}
                        align="end"
                        className="nav-menu-dropdown--redesign nav-menu-dropdown--panel"
                        hoverEnabled={false}
                        icon={<AccountIcon className="nav-menu-link-icon" aria-hidden="true" />}
                        id="account-nav-dropdown"
                        label="Account"
                        groupLabels={authenticatedAccountMenuGroupLabels}
                        itemGroups={authenticatedAccountMenuGroups}
                        onSelect={() => setExpanded(false)}
                        theme={navbarRedesignTheme}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <Nav.Link
                      href={headerLoginPath}
                      aria-expanded={location.pathname === '/login'}
                      aria-haspopup="dialog"
                      className="nav-login-link"
                      onClick={(e) => {
                        e.preventDefault();
                        closeNavbar();
                        handleLogin();
                      }}
                    >
                      <span>Log in</span>
                    </Nav.Link>
                    {!isNavbarMobileView && (
                      <Nav.Link
                        href={headerSignupPath}
                        aria-expanded={location.pathname === '/login'}
                        aria-haspopup="dialog"
                        className="nav-signup-link"
                        onClick={(event) => {
                          event.preventDefault();
                          closeNavbar();
                          handleSignup();
                        }}
                      >
                        <span>Sign up</span>
                      </Nav.Link>
                    )}
                  </>
                )}
              </Nav>

              {/* Custom toggle button that changes to X when menu is open */}
              <Navbar.Toggle
                ref={navbarToggleRef}
                aria-controls="basic-navbar-nav"
                aria-expanded={expanded}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    navbarOpenModalityRef.current = 'keyboard';
                  }
                }}
                onPointerDown={() => {
                  navbarOpenModalityRef.current = 'pointer';
                }}
              >
                <span className="navbar-toggler-glyph" aria-hidden="true">
                  <span />
                  <span />
                </span>
              </Navbar.Toggle>

              <Navbar.Collapse
                ref={navbarCollapseRef}
                id="basic-navbar-nav"
                className="navbar-collapse--redesign"
              >
                <Nav className="desktop-menu desktop-menu--redesign">
                  {isAuthenticated ? (
                    <>
                      <Nav.Link
                        as={NavLink}
                        to="/ai-agent"
                        onMouseEnter={() => loadAiAgent().catch(() => undefined)}
                        onFocus={() => loadAiAgent().catch(() => undefined)}
                        onClick={closeNavbar}
                      >
                        <AgentIcon className="nav-menu-link-icon" aria-hidden="true" />
                        <span>AI Agent</span>
                      </Nav.Link>
                      <Nav.Link as={NavLink} to="/marketplace" onClick={closeNavbar}>
                        <MarketplaceIcon className="nav-menu-link-icon" aria-hidden="true" />
                        <span>Marketplace</span>
                      </Nav.Link>
                      <NavMenuDropdown
                        active={isEducationRouteActive}
                        className="nav-menu-dropdown--redesign nav-menu-dropdown--panel"
                        groupLabels={['Programs', 'Resources']}
                        hoverEnabled={!isNavbarCollapsedView}
                        icon={<EducationIcon className="nav-menu-link-icon" aria-hidden="true" />}
                        id="education-nav-dropdown"
                        label="Education"
                        itemGroups={educationMenuGroups}
                        onSelect={() => setExpanded(false)}
                        theme={navbarRedesignTheme}
                      />
                      <Nav.Link as={NavLink} to="/dashboard" onClick={closeNavbar}>
                        <DashboardIcon className="nav-menu-link-icon" aria-hidden="true" />
                        <span>Dashboard</span>
                      </Nav.Link>
                      {isNavbarMobileView && (
                        <NavMenuDropdown
                          className="nav-menu-dropdown--redesign nav-menu-dropdown--sheet"
                          hoverEnabled={false}
                          icon={<AccountIcon className="nav-menu-link-icon" aria-hidden="true" />}
                          id="account-nav-dropdown"
                          label="Account"
                          groupLabels={authenticatedAccountMenuGroupLabels}
                          itemGroups={authenticatedAccountMenuGroups}
                          onSelect={() => setExpanded(false)}
                          theme={navbarRedesignTheme}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {aiStrategyNavLink}
                      {marketplaceNavLink}
                      <NavMenuDropdown
                        active={isEducationRouteActive}
                        className="nav-menu-dropdown--redesign nav-menu-dropdown--panel"
                        groupLabels={['Programs', 'Resources']}
                        icon={<EducationIcon className="nav-menu-link-icon" aria-hidden="true" />}
                        hoverEnabled={!isNavbarCollapsedView}
                        id="education-nav-dropdown"
                        label="Education"
                        itemGroups={educationMenuGroups}
                        onSelect={() => setExpanded(false)}
                        theme={navbarRedesignTheme}
                      />
                      <Nav.Link
                        as={NavLink}
                        to="/enterprise"
                        className="nav-section-start"
                        onClick={closeNavbar}
                      >
                        <span>Enterprise</span>
                      </Nav.Link>
                      <Nav.Link as={NavLink} to="/pricing" onClick={closeNavbar}>
                        <span>Pricing</span>
                      </Nav.Link>
                      <Nav.Link as={NavLink} to="/affiliate" onClick={closeNavbar}>
                        <span>Affiliate</span>
                      </Nav.Link>
                      {isNavbarMobileView && (
                        <Nav.Link
                          href={headerSignupPath}
                          aria-expanded={location.pathname === '/login'}
                          aria-haspopup="dialog"
                          className="nav-mobile-signup-link"
                          onClick={(event) => {
                            event.preventDefault();
                            closeNavbar();
                            handleSignup();
                          }}
                        >
                          <span>Sign up</span>
                        </Nav.Link>
                      )}
                    </>
                  )}
                </Nav>
              </Navbar.Collapse>
            </Container>
          </Navbar>
        </header>
      )}

      <main
        className={[
          'app-main',
          !shouldRenderHeader ? 'no-header' : '',
          usesCompactWorkspaceShell ? 'app-main--compact-workspace' : '',
          isAiAgentPage ? 'app-main--ai-agent' : '',
          isHomeLovableRoute ? 'app-main--home-lovable' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <Suspense fallback={<Loading />}>
          <Routes location={routeLocation}>
            {/* ⚡ PERFORMANCE: Lazy-loaded marketplace routes with Suspense for code splitting */}
            <Route
              path="/marketplace"
              element={
                <Suspense fallback={<MarketplaceRouteFallback />}>
                  <MarketplacePage />
                </Suspense>
              }
            />
            <Route
              path="/marketplace/strategy/:id"
              element={
                <Suspense fallback={<Loading />}>
                  <StrategyDetailsPage />
                </Suspense>
              }
            />
            <Route
              path="/marketplace/account/:id"
              element={
                <Suspense fallback={<Loading />}>
                  <MarketplaceAccountRedirectPage />
                </Suspense>
              }
            />
            <Route
              path="/marketplace/strategy/:id/settings"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<Loading />}>
                    <StrategySettingsPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* <Route path="/my-published-strategies" element={<ProtectedRoute><MyPublishedStrategies /></ProtectedRoute>} /> */}
            {/* Anonymous visitors paint immediately. Returning sessions keep a workspace splash. */}
            <Route
              path="/"
              element={
                rootLandingRoute === 'loading' ? (
                  <WorkspaceRouteFallback label="workspace" />
                ) : rootLandingRoute === 'dashboard' ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <Suspense fallback={null}>
                    <HomepageExperiment />
                  </Suspense>
                )
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<WorkspaceRouteFallback label="dashboard" />}>
                    <AuthenticatedDashboard />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* Deterministic legacy review route; no production experiment assignment. */}
            <Route path="/home-lovable" element={<HomeLovable />} />
            {/* Deterministic V3 review route; the root experiment uses this as control. */}
            <Route
              path="/home-v3"
              element={
                <Suspense fallback={<Loading />}>
                  <HomeV3 />
                </Suspense>
              }
            />
            {/* Deterministic V4 review route; the root experiment uses this as test. */}
            <Route
              path="/home-v4"
              element={
                <Suspense fallback={<Loading />}>
                  <HomeV4 />
                </Suspense>
              }
            />
            {/* Clarity-first Control Room review route. Not in the live homepage experiment. */}
            <Route
              path="/home-clarity"
              element={
                <Suspense fallback={<Loading />}>
                  <ClarityHome />
                </Suspense>
              }
            />
            {/* Legacy homepage variants for comparison/rollback */}
            <Route path="/home-control" element={<Home />} />
            <Route path="/home-trust-strip" element={<HomeWithTrustStrip />} />
            <Route path="/home-old" element={<HomeWithABTest />} />
            <Route
              path="/my-bots"
              element={
                <ProtectedWorkspaceRoute label="bots">
                  <MyDeploymentsPage />
                </ProtectedWorkspaceRoute>
              }
            />
            <Route
              path="/brokerage-accounts"
              element={
                <ProtectedWorkspaceRoute label="brokerage accounts">
                  <BrokerAccountsPage />
                </ProtectedWorkspaceRoute>
              }
            />
            <Route
              path="/brokerage-accounts/:brokerAccountId"
              element={
                <ProtectedWorkspaceRoute label="brokerage account">
                  <BrokerAccountDetailsPage />
                </ProtectedWorkspaceRoute>
              }
            />
            <Route path="/bot/:id" element={<BotDetails />} />
            <Route
              path="/user_profile"
              element={<Navigate to="/account-settings/profile" replace />}
            />
            <Route
              path="/account-settings/*"
              element={
                <ProtectedRoute>
                  <AccountSettings contextualNavigation={shouldRenderAuthenticatedSidebar} />
                </ProtectedRoute>
              }
            />
            {/* Education routes */}
            <Route path="/weekly-workshop" element={<WeeklyWorkshop />} />
            <Route
              path="/live-algo-lab"
              element={
                <EducationDesignExperiment>
                  <LiveAlgoLabSalesPage />
                </EducationDesignExperiment>
              }
            />
            <Route
              path="/live-algo-lab/watch"
              element={
                <EducationDesignExperiment>
                  <LiveAlgoLabWatchPage />
                </EducationDesignExperiment>
              }
            />
            {/* AI Bootcamp public sales pages */}
            <Route
              path="/ai-bot-builder-bootcamp/sales"
              element={<EducationCompatibilityRedirect to="/courses/ai-trading-bootcamp" />}
            />
            <Route
              path="/ai-bot-builder-bootcamp/watch"
              element={
                <ProtectedRoute>
                  <AIBootcampProPortal />
                </ProtectedRoute>
              }
            />
            {/* CANONICAL course routes. */}
            <Route
              path="/courses/ai-trading-bootcamp"
              element={
                <EducationDesignExperiment>
                  <CourseNameExperimentPage />
                </EducationDesignExperiment>
              }
            />
            <Route
              path="/courses/ai-trading-bootcamp/self-paced"
              element={
                <EducationDesignExperiment>
                  <CourseSelfPacedPage />
                </EducationDesignExperiment>
              }
            />
            {/* Deterministic review routes do not enroll visitors in the experiment. */}
            <Route
              path="/courses/ai-trading-bootcamp/control"
              element={<CoursePage variantKey="control" />}
            />
            <Route
              path="/courses/ai-trading-bootcamp/agentic"
              element={<CoursePage variantKey="agentic" />}
            />
            {/* Clarity review of the PostHog bootcamp test. Not enrolled. */}
            <Route path="/courses-clarity" element={<CourseClarityReview />} />
            <Route path="/courses-clarity/:slug" element={<CourseClarityReview />} />
            {/* One challenge at a time: /challenges resolves to the current run.
              Slug pages stay so emailed links and past recordings keep working. */}
            <Route
              path="/challenges"
              element={
                <EducationDesignExperiment>
                  <NextChallengePage />
                </EducationDesignExperiment>
              }
            />
            <Route
              path="/challenges/:slug"
              element={
                <EducationDesignExperiment>
                  <ChallengePage />
                </EducationDesignExperiment>
              }
            />
            <Route
              path="/challenges/:slug/welcome"
              element={
                <Suspense fallback={<Loading />}>
                  <ChallengeBookingPage />
                </Suspense>
              }
            />
            <Route
              path="/challenges/:slug/call-confirmed"
              element={
                <Suspense fallback={<Loading />}>
                  <ChallengeCallConfirmedPage />
                </Suspense>
              }
            />
            {/* Clarity review of the PostHog challenge test. Not enrolled. */}
            <Route path="/challenges-clarity" element={<ChallengeClarityReview />} />
            <Route path="/challenges-clarity/:slug" element={<ChallengeClarityReview />} />
            {/* Legacy campaign entry points. These are in already-sent emails and on
              external sites, so they must keep working forever. The redirect
              preserves query (promoCode, utm_*) and fragment. */}
            <Route
              path="/ai-bot-builder-bootcamp/self-paced"
              element={
                <EducationCompatibilityRedirect to="/courses/ai-trading-bootcamp/self-paced" />
              }
            />
            <Route
              path="/ai-bot-builder-bootcamp"
              element={<EducationCompatibilityRedirect to="/courses/ai-trading-bootcamp" />}
            />
            <Route
              path="/ai-trading-course"
              element={<EducationCompatibilityRedirect to="/courses/ai-trading-bootcamp" />}
            />
            <Route
              path="/courses/ai-trading"
              element={<EducationCompatibilityRedirect to="/courses/ai-trading-bootcamp" />}
            />
            <Route
              path="/courses/ai-investing"
              element={<EducationCompatibilityRedirect to="/courses/ai-trading-bootcamp" />}
            />
            {/* Paid features */}
            <Route
              path="/add-bot"
              element={
                <ProtectedRoute requirePayment={true}>
                  <AddBot />
                </ProtectedRoute>
              }
            />
            {/*
            SHARING ROUTES - Dedicated share page for viewing shared strategies
            No authentication required - anyone with a valid share link can view.
            Share URLs: /share/{id}?share={token} or /share/{id}?public=true
          */}
            <Route path="/share/:aiStrategyId" element={<SharePage />} />
            {/*
            /ai-bot-builder routes use ShareRedirectHandler for backward compatibility.
            - If URL has ?share= or ?public=true → redirects to /share/ route
            - Otherwise → renders AiAgent with normal auth protection
          */}
            <Route
              path="/ai-bot-builder/:aiStrategyId?"
              element={
                <Suspense fallback={<AiAgentRouteFallback />}>
                  <ShareRedirectHandler AiAgentComponent={AiAgent} />
                </Suspense>
              }
            />
            <Route
              path="/ai-agent/:conversationId?"
              element={
                <Suspense fallback={<AiAgentRouteFallback />}>
                  <ProtectedRoute requirePayment={true}>
                    <AiAgent />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} /> {/* Add this new route */}
            <Route
              path="/hackathon"
              element={
                <EducationDesignExperiment>
                  <Suspense fallback={<Loading />}>
                    <Contest />
                  </Suspense>
                </EducationDesignExperiment>
              }
            />
            <Route
              path="/hackathon/register"
              element={
                <EducationDesignExperiment>
                  <Suspense fallback={<Loading />}>
                    <ContestRegister />
                  </Suspense>
                </EducationDesignExperiment>
              }
            />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/affiliate" element={<AffiliatePage />} />
            <Route path="/affiliate/dashboard" element={<AffiliatePage />} />
            <Route path="/agents" element={<AgentsPage />} />
            <Route path="/compare" element={<ComparisonPage />} />
            <Route path="/compare/:slug" element={<ComparisonPage />} />
            <Route path="/brokers" element={<ComparisonPage />} />
            <Route path="/brokers/:brokerId" element={<ComparisonPage />} />
            <Route path="/guides" element={<ComparisonPage />} />
            <Route path="/guides/:slug" element={<ComparisonPage />} />
            <Route path="/developers" element={<Navigate to="/agents" replace />} />
            <Route
              path="/enterprise"
              element={
                <Suspense fallback={<Loading />}>
                  <EnterprisePage />
                </Suspense>
              }
            />
            <Route
              path="/ai-workflow-audit"
              element={
                <Suspense fallback={<Loading />}>
                  <FamilyOfficeAiAuditPage />
                </Suspense>
              }
            />
            <Route
              path="/case-studies"
              element={
                <Suspense fallback={<Loading />}>
                  <EducationDesignExperiment>
                    <SampleComplexBacktestPage />
                  </EducationDesignExperiment>
                </Suspense>
              }
            />
            <Route
              path="/case-studies/complex-backtests"
              element={<Navigate to="/case-studies" replace />}
            />
            <Route
              path="/sample-complex-backtest"
              element={<Navigate to="/case-studies" replace />}
            />
            <Route path="/investor" element={<InvestorPage />} />
            <Route
              path="/sales"
              element={<SalesPageWithABTest authenticated={isAuthenticated} />}
            />
            <Route
              path="/sales-ai-agent-entry"
              element={<SalesAiAgentEntryCompatibilityRedirect />}
            />
            <Route path="/sales-control" element={<SalesPage />} />
            <Route path="/sales05" element={<SalesPage05 />} />
            <Route path="/sales06" element={<SalesPage06 />} />
            <Route path="/sales07" element={<SalesPage07 />} />
            <Route path="/sales08" element={<SalesPage08 />} />
            <Route path="/lp/google-search" element={<GoogleSearchLandingPage />} />
            <Route path="/prompt-guide" element={<PromptGuide />} />
            <Route path="/sales-choose" element={<SalesChoosePage />} />
            <Route
              path="/sales-marketplace"
              element={
                <Suspense fallback={<Loading />}>
                  <SalesMarketplacePage />
                </Suspense>
              }
            />
            <Route path="/sales-ai-prompt" element={<SalesAiPromptPage />} />
            <Route
              path="/choose-plan"
              element={
                <ProtectedRoute>
                  <ChoosePlanPage />
                </ProtectedRoute>
              }
            />
            <Route path="/checkout/account" element={<CheckoutAuthPage />} />
            <Route path="/checkout/hosted-return" element={<HostedCheckoutReturnPage />} />
            <Route
              path="/checkout/starter-plan"
              element={
                <ProtectedRoute waitForPaymentStatus={false}>
                  <PlanCheckoutRoutePage
                    checkoutDesignVariantOverride={checkoutOverlayDesignVariant}
                    checkoutDesignExperimentActiveOverride={checkoutOverlayDesignVariant === 'test'}
                  />
                </ProtectedRoute>
              }
            />{' '}
            {/* Shared plan checkout */}
            <Route
              path="/checkout/light-plan"
              element={
                <ProtectedRoute waitForPaymentStatus={false}>
                  <PlanCheckoutRoutePage
                    checkoutDesignVariantOverride={checkoutOverlayDesignVariant}
                    checkoutDesignExperimentActiveOverride={checkoutOverlayDesignVariant === 'test'}
                  />
                </ProtectedRoute>
              }
            />{' '}
            {/* Shared plan checkout */}
            <Route
              path="/checkout/ai-builder"
              element={<Navigate to="/checkout/starter-plan" replace />}
            />{' '}
            {/* Legacy redirect */}
            <Route
              path="/checkout/bootcamp-upsell"
              element={<Navigate to="/challenges" replace />}
            />{' '}
            {/* Legacy compatibility redirect */}
            <Route
              path="/checkout"
              element={
                <ProtectedRoute waitForPaymentStatus={false}>
                  <CheckoutRoutePage
                    checkoutDesignVariantOverride={checkoutOverlayDesignVariant}
                    checkoutDesignExperimentActiveOverride={checkoutOverlayDesignVariant === 'test'}
                  />
                </ProtectedRoute>
              }
            />{' '}
            {/* Unified cart checkout */}
            <Route path="/feedback/why-not-join" element={<CartAbandonFeedbackPage />} />
            <Route path="/email/unsubscribe" element={<EmailUnsubscribePage />} />
            <Route path="/signup-survey" element={<SignupSurvey />} /> {/* Signup Survey Route */}
            <Route path="/post-signup" element={<PostSignupDecisionPage />} />
            <Route path="/auto-login-sent" element={<AutoLoginSentPage />} />{' '}
            {/* Auto-login sent page */}
            <Route path="/auto-login-test" element={<AutoLoginTest />} />{' '}
            {/* Auto-login test page */}
            <Route path="/seamless-login-demo" element={<SeamlessLoginDemo />} />{' '}
            {/* Seamless login demo */}
            <Route path="/modal-test" element={<ModalTestPage />} /> {/* Modal test page */}
            <Route path="/upgrade-to-pro" element={<UpgradeToProPage />} />
            <Route
              path="/upgrade-to-pro-control"
              element={<Navigate to="/upgrade-to-pro" replace />}
            />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <Navigate replace to={{ pathname: '/dashboard', search: location.search }} />
                </ProtectedRoute>
              }
            />{' '}
            {/* Legacy onboarding entry now converges on dashboard setup guide. */}
            <Route
              path="/video"
              element={
                <ProtectedRoute>
                  <VideoPage />
                </ProtectedRoute>
              }
            />{' '}
            {/* <-- PROTECTED VIDEO PAGE */}
            <Route path="/set-password" element={<SetPasswordPage />} />
            {/* Backtest route for linkedStrategyId (protected strategies) - query param based */}
            <Route
              path="/backtest"
              element={
                <ProtectedRoute>
                  <BacktestPage />
                </ProtectedRoute>
              }
            />
            {/* Backtest route with path parameters (regular strategies) */}
            <Route
              path="/backtest/:aiStrategyId/:revisionId"
              element={
                <ProtectedRoute>
                  <BacktestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/deploy"
              element={
                <ProtectedRoute>
                  <DeployPage />
                </ProtectedRoute>
              }
            />
            <Route path="/authorize-action/:actionId" element={<ActionAuthorizationPage />} />
            <Route path="/paywall-demo" element={<PaywallModalDemo />} />
            <Route path="/help" element={<Help />} />
            <Route path="/about" element={<TrustPage page="about" />} />
            <Route path="/contact" element={<TrustPage page="contact" />} />
            {/* /home for the home page */}
            <Route path="/home" element={<Home />} />
            {/* Blog routes */}
            <Route
              path="/blog"
              element={
                <EducationDesignExperiment>
                  <BlogIndex />
                </EducationDesignExperiment>
              }
            />
            {/* Keep the legacy hard-coded article until it is migrated into blog_post. */}
            <Route
              path="/blog/using-ai-to-make-ai-profitable-swing-trading-bot-in-seconds"
              element={
                <EducationDesignExperiment>
                  <UsingAIToMakeAProfitableSwingTradingBotInSeconds />
                </EducationDesignExperiment>
              }
            />
            <Route
              path="/blog/:slug"
              element={
                <EducationDesignExperiment>
                  <BlogPostPage />
                </EducationDesignExperiment>
              }
            />
            {/* Login route: compatibility URL rendered as overlay, not standalone page. */}
            <Route path="/login" element={<LoginRouteOverlay />} />
            {/* Add 404 catch-all route */}
            <Route path="*" element={<NotFound />} />
            <Route path="/profile/:userId" element={<UserProfilePage />} />
            <Route path="/backtest/:strategyId/:revisionId" element={<BacktestPage />} />
            {/* Update route for backtest chart view to use only backtestId */}
            <Route path="/backtest-chart/:backtestId" element={<BacktestChartPage />} />
            <Route path="/backtest-details/:backtestId" element={<BacktestChartPage />} />
            {/* Add new protected route for MyBacktestPage */}
            <Route
              path="/my-backtests"
              element={
                <ProtectedWorkspaceRoute label="backtests">
                  <MyBacktestPage />
                </ProtectedWorkspaceRoute>
              }
            />
            {/* My Strategies - dedicated page for viewing all strategies */}
            <Route
              path="/my-strategies"
              element={
                <ProtectedWorkspaceRoute label="strategies">
                  <MyStrategies />
                </ProtectedWorkspaceRoute>
              }
            />
            <Route path="/oauth/schwab/success" element={<SchwabSuccess />} />
            <Route path="/oauth/alpaca/success" element={<AlpacaSuccess />} />
            <Route path="/mobile/alpaca-auth/return" element={<AlpacaMobileReturnFallback />} />
            <Route path="/oauth/tradier/success" element={<TradierSuccess />} />
            <Route path="/connect/discord" element={<ConnectDiscord />} />
            <Route path="/connect/telegram" element={<ConnectTelegram />} />
            <Route path="/my-deployments" element={<Navigate to="/my-bots" replace />} />
            <Route
              path="/deployments/:deployId/logs"
              element={
                <ProtectedRoute>
                  <DeploymentRedirectPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/deployments/:deployId"
              element={
                <ProtectedRoute>
                  <DeploymentRedirectPage />
                </ProtectedRoute>
              }
            />
            <Route path="/auto-login-sent" element={<AutoLoginSentPage />} /> {/* Add new route */}
          </Routes>
        </Suspense>
      </main>

      {checkoutOverlayBackground && (
        <div
          className={cn(
            'checkout-route-overlay',
            isCheckoutAuthRoute && 'checkout-route-overlay--auth-first',
          )}
          role="presentation"
        >
          <div
            ref={checkoutDialogRef}
            className={cn(
              'checkout-route-overlay__dialog',
              isCheckoutAuthRoute && 'checkout-route-overlay__dialog--auth-first',
            )}
            role="dialog"
            aria-modal="true"
            aria-label={isCheckoutAuthRoute ? undefined : 'Checkout'}
            aria-labelledby={isCheckoutAuthRoute ? 'checkout-auth-visible-heading' : undefined}
            tabIndex={-1}
            data-bs-theme={isCheckoutAuthRoute ? 'dark' : accountMenuTheme}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className={cn(
                'checkout-route-overlay__close',
                isCheckoutAuthRoute && 'auth-first-close-control',
              )}
              aria-label={isCheckoutAuthRoute ? 'Close sign in' : 'Close checkout'}
              onClick={closeCheckoutOverlay}
            >
              <XIcon aria-hidden="true" />
            </button>
            <Suspense
              fallback={
                <CheckoutRedesignLoadingShell
                  checkoutTheme={accountMenuTheme}
                  presentation="overlay"
                />
              }
            >
              {isCheckoutOverlayResolving ? (
                <CheckoutOverlayLoading checkoutTheme={accountMenuTheme} />
              ) : (
                <Routes>
                  <Route path="/checkout/account" element={<CheckoutAuthPage />} />
                  <Route
                    path="/checkout/starter-plan"
                    element={
                      <ProtectedRoute
                        loadingFallback={
                          <CheckoutRedesignLoadingShell
                            checkoutTheme={accountMenuTheme}
                            presentation="overlay"
                          />
                        }
                        waitForPaymentStatus={false}
                      >
                        <PlanCheckoutRoutePage
                          presentation="overlay"
                          checkoutDesignVariantOverride="test"
                          checkoutDesignExperimentActiveOverride={
                            checkoutOverlayDesignVariant === 'test'
                          }
                        />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/checkout/light-plan"
                    element={
                      <ProtectedRoute
                        loadingFallback={
                          <CheckoutRedesignLoadingShell
                            checkoutTheme={accountMenuTheme}
                            presentation="overlay"
                          />
                        }
                        waitForPaymentStatus={false}
                      >
                        <PlanCheckoutRoutePage
                          presentation="overlay"
                          checkoutDesignVariantOverride="test"
                          checkoutDesignExperimentActiveOverride={
                            checkoutOverlayDesignVariant === 'test'
                          }
                        />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/checkout"
                    element={
                      <ProtectedRoute
                        loadingFallback={
                          <CheckoutRedesignLoadingShell
                            checkoutTheme={accountMenuTheme}
                            presentation="overlay"
                          />
                        }
                        waitForPaymentStatus={false}
                      >
                        <CheckoutRoutePage
                          presentation="overlay"
                          checkoutDesignVariantOverride="test"
                          checkoutDesignExperimentActiveOverride={
                            checkoutOverlayDesignVariant === 'test'
                          }
                        />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              )}
            </Suspense>
          </div>
        </div>
      )}

      {!shouldHideFooter && <AppFooter showGettingStartedLink={isAuthenticated} />}

      {/* Anonymous Spot sales chat. Only renders on the AI Trading Bootcamp
          course pages (see SPOT_ALLOWED_PATH_PREFIXES in the widget). */}
      <SpotChatWidget />
    </div>
  );
}

function App() {
  const location = useLocation();
  const shouldAutoLoadCheckoutCart = !isPlanCheckoutPath(location.pathname);

  useEffect(() => {
    // Analytics initialization is now handled in AppContent with user context
    console.debug('[App] Main app component mounted');
  }, []);

  return (
    <HelmetProvider>
      <CheckoutCartProvider autoLoad={shouldAutoLoadCheckoutCart}>
        <PublicRouteSeo />
        <NoindexRouteSeo />
        <AppContent />
      </CheckoutCartProvider>
    </HelmetProvider>
  );
}

export default App;
